import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radar, RadarChart as RechartsRadar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell } from 'recharts';
import confetti from 'canvas-confetti';
import './index.css';
import NewLandingPage from './NewLanding';
import {
  getDashboard, generateTasks, submitTask, getTaskHistory,
  getTaskReasoning, checkRisk,
  getCoachProfile, getTruthScore,
  getPlans, generatePlan, getStudentReports, generateReport,
  teacherLogin, getTeacherStudents, getTeacherStudentOverview, addTeacherNote,
  parentLogin, getParentDaily, addParentObservation, getParentAlerts,
  startOnboarding, submitAssessment, getFirstTask, getOnboardingStatus,
  getRetentionStatus, getRetentionMessages, markMessageRead,
  runFeedbackCycle, getFeedbackStatus,
  getFeedbackMetrics,
  getMetricsDashboard, computeMetrics,
  getAuditTrail,
  type Dashboard, type Task, type Gamification, type SubmitResult, type TaskHistory
} from './services/api';

// ============================================================
// CONSTANTS
// ============================================================
const STUDENT_ID = localStorage.getItem('edudos_student_id') || 'student_demo';

const TASK_TYPE_LABELS: Record<string, string> = {
  reality: 'Gerçeklik', micro: 'Mikro', ai_assisted: 'AI Destekli', production: 'Üretim',
};

const DIFFICULTY_LABELS: Record<string, string> = {
  very_easy: 'Çok Kolay', easy: 'Kolay', medium: 'Orta', hard: 'Zor', challenge: 'Meydan Okuma',
};

const LEVEL_ICONS: Record<number, string> = {
  1: '🌱', 2: '🔍', 3: '💪', 4: '🎯', 5: '⚡',
  6: '🛡️', 7: '🧠', 8: '👑', 9: '🏆', 10: '⭐',
};

// ============================================================
// MAIN APP — Responsive Layout
// ============================================================
type Screen = 'landing' | 'login' | 'home' | 'task' | 'coach' | 'progress' | 'profile' | 'ethics' |
  'plans' | 'reports' | 'teacher' | 'parent' | 'onboarding' | 'metrics' | 'retention';

export default function App() {
  const isLoggedIn = !!localStorage.getItem('edudos_student_id');
  const [screen, setScreen] = useState<Screen>('landing');
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [celebration, setCelebration] = useState<SubmitResult | null>(null);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moodState, setMoodState] = useState<{ mood_trend?: string; attention_score?: number; parent_negative_mood?: boolean } | null>(null);

  useEffect(() => {
    (window as any).__setScreen = setScreen;
    return () => { delete (window as any).__setScreen; };
  }, []);

  // Fetch mood/feedback status for mood-sensitive UI
  useEffect(() => {
    getFeedbackStatus(STUDENT_ID).then(setMoodState).catch(() => {});
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDashboard(STUDENT_ID);
      setDashboard(data);
      setError('');
    } catch (e: any) {
      setError('Bağlantı hatası. Tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const navigate = (s: Screen) => {
    setScreen(s);
    setSidebarOpen(false);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setScreen('task');
  };

  const handleSubmitSuccess = (result: SubmitResult) => {
    if (result.validation.completed) {
      setCelebration(result);
      setTimeout(() => { setCelebration(null); setScreen('home'); loadDashboard(); }, 3000);
    } else {
      setScreen('home');
      loadDashboard();
    }
  };

  const handleGenerateTasks = async () => {
    try {
      setLoading(true);
      await generateTasks(STUDENT_ID, { truth_score: 50, dominant_issue: 'none' }, '');
      await loadDashboard();
    } catch { setError('Görev oluşturulamadı'); }
  };

  const gam = dashboard?.gamification;

  const mainNav = [
    { key: 'home', icon: '🏠', label: 'Panel' },
    { key: 'coach', icon: '💬', label: 'Koçum' },
    { key: 'plans', icon: '📅', label: 'Planlar' },
    { key: 'progress', icon: '📊', label: 'İlerleme' },
    { key: 'reports', icon: '📋', label: 'Raporlar' },
  ];

  const extraNav = [
    { key: 'retention', icon: '🏆', label: 'Başarımlar' },
    { key: 'metrics', icon: '📈', label: 'Metrikler' },
    { key: 'teacher', icon: '👨‍🏫', label: 'Öğretmen' },
    { key: 'parent', icon: '👨‍👩‍👧', label: 'Veli' },
    { key: 'profile', icon: '👤', label: 'Profil' },
  ];

  // Mood-sensitive background class
  const moodBgClass = moodState?.mood_trend === 'declining' ? 'mood-bg-declining'
    : moodState?.mood_trend === 'improving' ? 'mood-bg-improving'
    : moodState?.parent_negative_mood ? 'mood-bg-anxious' : '';

  // Landing & Login — fullscreen, no sidebar (New Elite Design)
  if (screen === 'landing' || screen === 'login') {
    return <NewLandingPage
      isLoggedIn={isLoggedIn}
      onLogin={() => setScreen('login')}
      onDashboard={() => setScreen('home')}
      onExplore={() => {
        localStorage.setItem('edudos_student_id', 'student_demo');
        window.location.reload();
      }}
      onLoginSuccess={(id: string) => {
        localStorage.setItem('edudos_student_id', id);
        window.location.reload();
      }}
    />;
  }

  return (
    <div className={`app-layout ${moodBgClass}`}>
      {celebration && <CelebrationOverlay result={celebration} />}

      {/* Dynamic Background Blobs */}
      <DynamicBackground />

      {/* Mobile Overlay */}
      <div className={`mobile-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* SIDEBAR */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">⚖️</div>
          <div>
            <div className="sidebar-logo-text">Mizan EduOS</div>
            <div className="sidebar-logo-version">v7.0 — YARUKSAI</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Ana Menü</div>
          {mainNav.map(item => (
            <button key={item.key}
              className={`sidebar-item ${screen === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.key as Screen)}>
              <span className="sidebar-item-icon">{item.icon}</span>
              {item.label}
              {item.key === 'home' && dashboard?.active_tasks && dashboard.active_tasks.length > 0 && (
                <span className="sidebar-item-badge">{dashboard.active_tasks.length}</span>
              )}
            </button>
          ))}

          <div className="sidebar-section-label">Araçlar</div>
          {extraNav.map(item => (
            <button key={item.key}
              className={`sidebar-item ${screen === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.key as Screen)}>
              <span className="sidebar-item-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}

          <div className="sidebar-section-label">Sistem</div>
          <button className={`sidebar-item ${screen === 'ethics' ? 'active' : ''}`}
            onClick={() => navigate('ethics')}>
            <span className="sidebar-item-icon">⚖️</span> Etik İlkeler
          </button>
          <button className={`sidebar-item ${screen === 'onboarding' ? 'active' : ''}`}
            onClick={() => navigate('onboarding')}>
            <span className="sidebar-item-icon">🚀</span> Başlangıç
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user" onClick={() => navigate('profile')} style={{ cursor: 'pointer' }}>
            <div className="sidebar-avatar">{LEVEL_ICONS[gam?.level || 1] || '🌱'}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{STUDENT_ID}</div>
              <div className="sidebar-user-level">Lv.{gam?.level || 1} · {gam?.total_xp || 0} XP</div>
            </div>
          </div>
          <button className="sidebar-logout" onClick={() => navigate('landing')}
            style={{ marginBottom: 4, background: 'rgba(99,102,241,0.15)', borderColor: 'rgba(99,102,241,0.3)' }}>
            <span style={{ opacity: 0.7 }}>🏠</span> Ana Sayfa
          </button>
          <button className="sidebar-logout" onClick={() => {
            localStorage.removeItem('edudos_student_id');
            window.location.reload();
          }}>
            <span style={{ opacity: 0.7 }}>↩</span> Çıkış Yap
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="main-content">
        <div className="mobile-header">
          <button className="mobile-header-menu" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <span className="mobile-header-logo">Mizan EduOS</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {gam && gam.current_streak > 0 && (
              <span style={{ fontSize: 12, color: 'var(--streak-orange)', fontWeight: 600 }}>🔥{gam.current_streak}</span>
            )}
            <div className="sidebar-avatar" style={{ width: 30, height: 30, fontSize: 14 }}
              onClick={() => navigate('profile')}>
              {LEVEL_ICONS[gam?.level || 1] || '🌱'}
            </div>
          </div>
        </div>

        <div className="main-content-inner">
          {screen === 'home' && <HomeScreen dashboard={dashboard} loading={loading} error={error}
            onTaskClick={handleTaskClick} onGenerate={handleGenerateTasks} onRefresh={loadDashboard} onNavigate={navigate} moodState={moodState} />}
          {screen === 'task' && selectedTask && <TaskScreen task={selectedTask}
            onBack={() => { setScreen('home'); loadDashboard(); }} onSubmitSuccess={handleSubmitSuccess} />}
          {screen === 'coach' && <CoachScreen />}
          {screen === 'progress' && <ProgressScreen dashboard={dashboard} />}
          {screen === 'profile' && <ProfileScreen gamification={gam || null} />}
          {screen === 'ethics' && <EthicsScreen onBack={() => setScreen('home')} />}
          {screen === 'plans' && <PlansScreen />}
          {screen === 'reports' && <ReportsScreen />}
          {screen === 'teacher' && <TeacherScreen onBack={() => setScreen('home')} />}
          {screen === 'parent' && <ParentScreen onBack={() => setScreen('home')} />}
          {screen === 'onboarding' && <OnboardingScreen onComplete={() => { setScreen('home'); loadDashboard(); }} />}
          {screen === 'metrics' && <MetricsDashboardScreen />}
          {screen === 'retention' && <RetentionScreen />}
        </div>

        <div className="ethics-footer">
          Bu bir destek sistemidir. Psikolojik tanı koymaz.{' '}
          <a onClick={() => setScreen('ethics')}>Etik İlkeler</a>
        </div>
      </main>

      {/* MOBILE BOTTOM NAV */}
      <nav className="mobile-nav">
        <div className="mobile-nav-inner">
          {mainNav.map(item => (
            <button key={item.key}
              className={`mobile-nav-item ${screen === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.key as Screen)}>
              <span className="mobile-nav-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

// ============================================================
// HOME SCREEN
// ============================================================
function HomeScreen({ dashboard, loading, error, onTaskClick, onGenerate, onRefresh, onNavigate, moodState }: {
  dashboard: Dashboard | null; loading: boolean; error: string;
  onTaskClick: (t: Task) => void; onGenerate: () => void; onRefresh: () => void;
  onNavigate: (s: Screen) => void;
  moodState?: { mood_trend?: string; attention_score?: number; parent_negative_mood?: boolean } | null;
}) {
  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;
  if (error) return (
    <div className="empty-state">
      <div className="empty-icon">⚠️</div>
      <p>{error}</p>
      <button className="btn btn-primary" style={{ maxWidth: 220, margin: '16px auto' }} onClick={onRefresh}>Tekrar Dene</button>
    </div>
  );
  if (!dashboard) return null;

  const { gamification: gam, behavior, mood_message, active_tasks } = dashboard;

  // AI-driven personalized insight — MOOD-AWARE + DATA-DRIVEN + MOOD-SENSITIVE
  const getPersonalInsight = () => {
    const rate = behavior.completion_rate;
    const streak = gam.current_streak;
    const trend = behavior.truth_trend;
    const truthBonus = gam.truth_bonus_xp;
    const moodTrend = moodState?.mood_trend;
    const parentNeg = moodState?.parent_negative_mood;

    // MOOD-SENSITIVE: anxious/negative mood → empatik karşılama (en yüksek öncelik)
    if (parentNeg) return { emoji: '💙', text: 'Bugün her şey üst üste gelmiş olabilir. Gel seninle yükünü hafifletelim. Sana özel, sakin tempolu görevler hazırladım.', tag: 'Seninle Birlikteyim' };
    if (moodTrend === 'declining' && (trend === 'declining' || streak === 0)) return { emoji: '🫂', text: 'Zor bir dönemden geçtiğini hissediyorum. Bugün sana özel hafif görevlerle yavaşça başlayacağız. Kendine iyi davranmayı unutma.', tag: 'Seni Anlıyorum' };

    // Mood-Loop: declining trend + seri 0 = mutsuz öğrenci, generic motivasyon YASAK
    if (trend === 'declining' && streak === 0) return { emoji: '🫂', text: 'Son günlerde zor bir dönemden geçtiğini görüyorum. Bugün sana özel hafif görevlerle yavaşça başlayacağız. Kendine iyi davran.', tag: 'Seni Anlıyorum' };
    if (trend === 'declining' && rate < 0.5) return { emoji: '📉', text: `Tamamlama oranın %${Math.round(rate * 100)}'e düştü. Bu senin kapasiteni yansıtmıyor — bugün stratejiyi değiştiriyoruz. Mikro görevlerle güvenini geri kazanacağız.`, tag: 'Kişisel Analiz' };
    if (rate < 0.3) return { emoji: '🔍', text: `Son %${Math.round(rate * 100)} oran, görevlerin zorluğunun ayarlanması gerektiğini gösteriyor. Bugün BJ Fogg'un "Tiny Habits" ilkesini uyguluyoruz — en küçük adımla başla.`, tag: 'Bilimsel Ayarlama' };
    if (streak === 0 && behavior.total_tasks > 5) return { emoji: '🔄', text: `Serin koptu — ama ${behavior.completed} görev tamamlamış biri olarak kapasiten ortada. Carol Dweck'in dediği gibi: "Başarısızlık, öğrenmenin ta kendisi." Yeniden başlıyoruz.`, tag: 'Geri Dönüş' };
    if (streak >= 7) return { emoji: '⚡', text: `${streak} günlük seri! Beynin artık bu rutini "ödül" olarak kodluyor (Dopamin döngüsü aktif). Bugün zorluk seviyesini %20 artırıyorum — büyüme zamanı.`, tag: 'Nörobilim: Momentum' };
    if (streak >= 3 && rate >= 0.7) return { emoji: '🔥', text: `${streak} gün üst üste + %${Math.round(rate * 100)} başarı. Fogg Davranış Modeli'nde "motivasyon + yetenek + tetikleyici" üçlüsünü yakaladın. Lv.${gam.level + 1}'e ${gam.xp_to_next} XP.`, tag: 'Bilimsel İvme' };
    if (rate >= 0.8 && truthBonus > 0) return { emoji: '🎯', text: `%${Math.round(rate * 100)} başarı + ${truthBonus} dürüstlük XP'si. Dweck'in "Growth Mindset" tanımının canlı örneğisin. Bugün derinleşme zamanı.`, tag: 'Yüksek Performans' };
    if (rate >= 0.6) return { emoji: '🧠', text: mood_message || `Dengeli bir gün — %${Math.round(rate * 100)} oranla istikrarlı ilerliyorsun. Bugün tutarlılık > hız.`, tag: 'Günlük Analiz' };
    return { emoji: '🌱', text: mood_message || 'Her adım bir ileri. Bugün bir görev tamamla — başlangıç her şeyden önemli.', tag: 'Günün Mesajı' };
  };

  const insight = getPersonalInsight();

  // Sort tasks: highest XP first = "ana görev"
  const sortedTasks = [...active_tasks].sort((a, b) => b.xp_reward - a.xp_reward);
  const mainTask = sortedTasks[0];
  const supportTasks = sortedTasks.slice(1);

  // Mood-sensitive screen title
  const moodTitle = moodState?.parent_negative_mood ? 'Bugün Sakin Bir Gün'
    : moodState?.mood_trend === 'declining' ? 'Bugün Seninleyim'
    : moodState?.mood_trend === 'improving' ? 'Bugün Güçlü Hissediyorsun!'
    : 'Bugünüm';

  return (
    <div className="animate-slide-up">
      <div className="screen-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="screen-title">{moodTitle}</div>
            <div className="screen-subtitle">
              {new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          {gam.current_streak > 0 && <div className="streak-badge">🔥 {gam.current_streak} gün</div>}
        </div>
      </div>

      {/* AI Personal Insight — not generic motivation, data-driven */}
      <div className="mood-banner" style={{ borderLeft: '3px solid var(--accent)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 28, lineHeight: 1 }}>{insight.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--accent-light)', marginBottom: 4, position: 'relative', zIndex: 1 }}>
              {insight.tag}
            </div>
            <div className="mood-text" style={{ fontSize: 14 }}>{insight.text}</div>
          </div>
        </div>
      </div>

      <XPBar gamification={gam} />

      {sortedTasks.length > 0 ? (
        <>
          {/* ANA GÖREV — büyük, vurgulu */}
          <div className="section-header">
            <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--accent-light)' }}>🔥</span> Ana Görev
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div className="task-card main-task-card" onClick={() => onTaskClick(mainTask)}
              style={{ border: '1px solid rgba(124,58,237,0.35)', boxShadow: '0 0 30px rgba(124,58,237,0.12)', position: 'relative', overflow: 'hidden' }}>
              {/* Glow accent line */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #7c3aed, #06b6d4)', borderRadius: '14px 14px 0 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingTop: 4 }}>
                <span className={`task-type-badge task-type-${mainTask.type}`}>
                  {TASK_TYPE_LABELS[mainTask.type] || mainTask.type}
                </span>
                <span style={{ fontSize: 11, color: 'var(--accent-light)', fontWeight: 700, padding: '3px 12px', background: 'rgba(124,58,237,0.15)', borderRadius: 12, letterSpacing: 0.5 }}>
                  ⭐ ÖNCELİK
                </span>
              </div>
              <div className="task-text" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{mainTask.text}</div>
              {/* Psychological purpose — always visible */}
              {mainTask.psychological_purpose && (
                <div style={{ fontSize: 12, color: 'var(--info)', padding: '6px 10px', background: 'rgba(6,182,212,0.06)', borderRadius: 8, marginBottom: 8, lineHeight: 1.4 }}>
                  💡 {mainTask.psychological_purpose}
                </div>
              )}
              {/* Data-driven scientific reason */}
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 12px', background: 'rgba(124,58,237,0.06)', borderRadius: 8, marginBottom: 12, lineHeight: 1.5, borderLeft: '2px solid var(--accent)' }}>
                🧠 {behavior.completion_rate < 0.5
                  ? `Tamamlama oranın %${Math.round(behavior.completion_rate * 100)} — BJ Fogg: "Küçük kazanımlar güven inşa eder." Bu görev tam bunu yapacak.`
                  : gam.current_streak >= 3
                  ? `${gam.current_streak} günlük serin dopamin döngüsünü aktif tuttu. Bu görev seviyen için kritik — Lv.${gam.level + 1}'e ${gam.xp_to_next} XP.`
                  : `Lv.${gam.level} → Lv.${gam.level + 1}: ${gam.xp_to_next} XP kaldı. Dweck: "Çaba, yeteneği aktive eden anahtardır."`}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>⏱ {mainTask.time_limit_minutes} dk · {DIFFICULTY_LABELS[mainTask.difficulty] || mainTask.difficulty}</span>
                <span className="task-xp" style={{ fontSize: 15, fontWeight: 700 }}>+{mainTask.xp_reward} XP</span>
              </div>
              {/* CTA — en önemli aksiyon, hiçbir şeyle yarışmamalı */}
              <button className="btn btn-primary main-task-cta"
                style={{ width: '100%', padding: '14px', fontSize: 15, fontWeight: 700, borderRadius: 12, background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', border: 'none', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 16px rgba(124,58,237,0.3)', letterSpacing: 0.3 }}
                onClick={(e) => { e.stopPropagation(); onTaskClick(mainTask); }}>
                Görevi Başlat →
              </button>
            </div>
          </div>

          {/* DESTEK GÖREVLERİ */}
          {supportTasks.length > 0 && (
            <>
              <div className="section-header">
                <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--xp-gold)' }}>🟡</span> Destek Görevler
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{supportTasks.length} görev</span>
              </div>
              {supportTasks.map(task => (
                <TaskCard key={task.task_id} task={task} onClick={() => onTaskClick(task)} />
              ))}
            </>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">✨</div>
          <p style={{ marginBottom: 16 }}>
            {behavior.total_tasks > 0 ? 'Tüm görevlerini tamamladın! Yeni görev oluştur.' : 'Henüz görev yok. İlk görevini oluştur!'}
          </p>
          <button className="btn btn-primary" style={{ maxWidth: 280, margin: '0 auto' }} onClick={onGenerate}>
            Günlük Görevlerimi Oluştur
          </button>
        </div>
      )}

      {behavior.total_tasks > 0 && (
        <div className="stat-grid" style={{ marginTop: 20 }}>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--success)' }}>{behavior.completed}</div>
            <div className="stat-label">Tamamlanan</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{Math.round(behavior.completion_rate * 100)}%</div>
            <div className="stat-label">Başarı</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--xp-gold)' }}>{gam.total_xp}</div>
            <div className="stat-label">Toplam XP</div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div className="section-header"><div className="section-title">Hızlı Erişim</div></div>
        <div className="quick-grid">
          {[
            { icon: '👨‍🏫', label: 'Öğretmen', key: 'teacher' },
            { icon: '👨‍👩‍👧', label: 'Veli', key: 'parent' },
            { icon: '📈', label: 'Metrikler', key: 'metrics' },
            { icon: '🏆', label: 'Başarımlar', key: 'retention' },
            { icon: '👤', label: 'Profil', key: 'profile' },
            { icon: '⚖️', label: 'Etik', key: 'ethics' },
          ].map(q => (
            <button key={q.key} className="quick-btn" onClick={() => onNavigate(q.key as Screen)}>
              <span className="quick-btn-icon">{q.icon}</span> {q.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// XP BAR
// ============================================================
function XPBar({ gamification: gam }: { gamification: Gamification }) {
  return (
    <div className="xp-container">
      <div className="xp-header">
        <span className="xp-level">Lv.{gam.level} {gam.level_name}</span>
        <span className="xp-numbers">{gam.xp_in_level} / {gam.xp_in_level + gam.xp_to_next} XP</span>
      </div>
      <div className="xp-bar-bg">
        <div className="xp-bar-fill" style={{ width: `${gam.progress_pct}%` }} />
      </div>
    </div>
  );
}

// ============================================================
// TASK CARD
// ============================================================
function TaskCard({ task, onClick }: { task: Task; onClick: () => void }) {
  const [showReasoning, setShowReasoning] = useState(false);
  const [reasoning, setReasoning] = useState<string | null>(null);

  // Bilimsel Açıklanabilirlik Katmanı — BJ Fogg, Dweck, Pomodoro, Spacing Effect
  const getScientificContext = (t: Task): string => {
    const contexts: string[] = [];
    // Görev türüne göre bilimsel çerçeve
    if (t.type === 'micro') contexts.push('🧪 BJ Fogg — Tiny Habits: Küçük görevler alışkanlık döngüsünü başlatır.');
    if (t.type === 'reality') contexts.push('🔬 Gerçeklik Kontrolü: Bildiğini sandığın şeyi kanıtla — "illusion of competence" engelini kır.');
    if (t.type === 'ai_assisted') contexts.push('🤖 AI Destekli Öğrenme: Socratic metod — cevabı bulmak yerine doğru soruyu sor.');
    if (t.type === 'production') contexts.push('🏗️ Üretim Görevi: Bloom\'un Taksonomisi\'nde en üst katman — bilgiyi "uygula ve yarat".');
    // Zorluk seviyesine göre
    if (t.difficulty === 'very_easy' || t.difficulty === 'easy') contexts.push('📐 Vygotsky — Zone of Proximal Development: Biraz zorlayıcı ama ulaşılabilir.');
    if (t.difficulty === 'hard' || t.difficulty === 'challenge') contexts.push('💪 Deliberate Practice (Ericsson): Konfor alanının hemen dışında çalışmak büyümeyi tetikler.');
    // Süre kontrolü
    if (t.time_limit_minutes <= 15) contexts.push('⏱️ Pomodoro Tekniği: Kısa odak pencereleri derin öğrenmeyi artırır.');
    if (t.time_limit_minutes >= 30) contexts.push('🧠 Deep Work (Cal Newport): Uzun odak süresi karmaşık konularda gereklidir.');
    return contexts.join('\n');
  };

  const handleReasoningClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (reasoning) { setShowReasoning(!showReasoning); return; }
    try {
      const data = await getTaskReasoning(task.task_id);
      setReasoning(data.explanation);
      setShowReasoning(true);
    } catch {
      const fallback = task.reasoning
        ? (() => { try { return JSON.parse(task.reasoning).user_explanation; } catch { return null; } })()
        : null;
      setReasoning(fallback || task.psychological_purpose || 'Bu görev senin için özel olarak seçildi.');
      setShowReasoning(true);
    }
  };

  // Laboratuvar Raporu: bilimsel çerçeve kartları
  const getLabCards = (t: Task): { framework: string; author: string; finding: string; relevance: string }[] => {
    const cards: { framework: string; author: string; finding: string; relevance: string }[] = [];
    if (t.type === 'micro') cards.push({ framework: 'Tiny Habits Modeli', author: 'BJ Fogg, Stanford', finding: 'Küçük davranışlar, alışkanlık döngüsünü tetikleyen en güçlü mekanizmadır.', relevance: 'Bu mikro görev, minimum eforla maksimum alışkanlık kazanımı sağlar.' });
    if (t.type === 'reality') cards.push({ framework: 'Metacognition & Illusion of Competence', author: 'Dunning-Kruger Araştırması', finding: 'İnsanlar bildiklerini sandıkları konularda sistematik yanılgıya düşer.', relevance: 'Gerçeklik kontrolü, bilgi yanılsamasını kırar ve gerçek öğrenmeyi başlatır.' });
    if (t.type === 'ai_assisted') cards.push({ framework: 'Socratic Method', author: 'Platon → Paul & Elder (2006)', finding: 'Cevabı vermek yerine doğru soruyu sormak, derin düşünmeyi tetikler.', relevance: 'AI destekli etkileşim, eleştirel düşünme kaslarını çalıştırır.' });
    if (t.type === 'production') cards.push({ framework: "Bloom'un Taksonomisi — Üst Katman", author: 'Benjamin Bloom (1956)', finding: '"Hatırla → Anla → Uygula → Analiz Et → Değerlendir → Yarat" hiyerarşisinin en üstü.', relevance: 'Üretim görevi, bilgiyi sentezleyerek kalıcı hale getirir.' });
    if (t.difficulty === 'very_easy' || t.difficulty === 'easy') cards.push({ framework: 'Zone of Proximal Development', author: 'Lev Vygotsky (1978)', finding: 'Öğrenme, mevcut seviyenin hemen üstündeki bölgede gerçekleşir.', relevance: 'Zorluk seviyesi senin ZPD bölgene göre ayarlandı.' });
    if (t.difficulty === 'hard' || t.difficulty === 'challenge') cards.push({ framework: 'Deliberate Practice', author: 'K. Anders Ericsson (1993)', finding: 'Konfor alanının hemen dışında, bilinçli tekrar büyümeyi tetikler.', relevance: 'Bu görev seni zorluyor çünkü büyüme ancak böyle gerçekleşir.' });
    if (t.time_limit_minutes <= 15) cards.push({ framework: 'Pomodoro Tekniği', author: 'Francesco Cirillo (1980s)', finding: 'Kısa odak pencereleri (15-25dk) derin öğrenmeyi ve bilgi kalıcılığını artırır.', relevance: `${t.time_limit_minutes} dakikalık odak penceresi — kısa ama etkili.` });
    if (t.time_limit_minutes >= 30) cards.push({ framework: 'Deep Work Prensibi', author: 'Cal Newport (2016)', finding: 'Kesintisiz uzun odak süreleri, karmaşık konularda zorunludur.', relevance: `${t.time_limit_minutes} dakikalık derin çalışma — yüzeysel öğrenme değil, gerçek kavrayış.` });
    if (cards.length === 0) cards.push({ framework: 'Growth Mindset', author: 'Carol Dweck (2006)', finding: '"Yetenek sabit değildir, çaba ile gelişir." — 30 yıllık Stanford araştırması.', relevance: 'Bu görev, gelişim odaklı düşünce yapısını pekiştirir.' });
    return cards;
  };

  return (
    <div className="task-card" onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span className={`task-type-badge task-type-${task.type}`}>
          {TASK_TYPE_LABELS[task.type] || task.type}
        </span>
        <button onClick={handleReasoningClick} className="btn-ghost"
          style={{ padding: '4px 10px', fontSize: 11, borderRadius: 16 }}>🔬 Neden?</button>
      </div>
      <div className="task-text">{task.text}</div>
      {task.psychological_purpose && (
        <div style={{ fontSize: 12, color: 'var(--info)', padding: '6px 10px', background: 'rgba(6,182,212,0.06)', borderRadius: 8, marginBottom: 8, lineHeight: 1.4 }}>
          💡 {task.psychological_purpose}
        </div>
      )}
      {/* LABORATUVAR RAPORU MODAL */}
      {showReasoning && (
        <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); setShowReasoning(false); }}>
          <div className="modal-content lab-report-modal" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 420, borderTop: '3px solid #7c3aed', position: 'relative' }}>
            {/* Header — akademik rapor stili */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: '#7c3aed', marginBottom: 4 }}>
                  🔬 Laboratuvar Raporu
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                  Görev Analiz Belgesi
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                  Rapor No: {task.task_id.substring(0, 8).toUpperCase()} · Tarih: {new Date().toLocaleDateString('tr-TR')}
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setShowReasoning(false); }}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
            </div>

            {/* Bölüm 1: AI Gerekçesi */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--info)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 20, height: 1, background: 'var(--info)', display: 'inline-block' }} />
                Bölüm 1: AI Gerekçesi
              </div>
              <div style={{ padding: '12px 14px', background: 'rgba(6,182,212,0.06)', borderRadius: 10, borderLeft: '2px solid var(--info)', fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                {reasoning || task.psychological_purpose || 'Bu görev kişisel profiline göre seçildi.'}
              </div>
            </div>

            {/* Bölüm 2: Bilimsel Kanıt Kartları */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#7c3aed', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 20, height: 1, background: '#7c3aed', display: 'inline-block' }} />
                Bölüm 2: Bilimsel Kanıt
              </div>
              {getLabCards(task).map((card, ci) => (
                <div key={ci} style={{ padding: '12px 14px', background: 'rgba(124,58,237,0.04)', borderRadius: 10, border: '1px solid rgba(124,58,237,0.12)', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>📄 {card.framework}</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, marginBottom: 6 }}>{card.author}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 6, fontStyle: 'italic' }}>
                    "{card.finding}"
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5, padding: '6px 10px', background: 'rgba(124,58,237,0.06)', borderRadius: 6 }}>
                    → <strong>Senin için:</strong> {card.relevance}
                  </div>
                </div>
              ))}
            </div>

            {/* Bölüm 3: Görev Parametreleri */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--success)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 20, height: 1, background: 'var(--success)', display: 'inline-block' }} />
                Bölüm 3: Görev Parametreleri
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Zorluk</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{DIFFICULTY_LABELS[task.difficulty] || task.difficulty}</div>
                </div>
                <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Süre</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{task.time_limit_minutes} dakika</div>
                </div>
                <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Tür</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{TASK_TYPE_LABELS[task.type] || task.type}</div>
                </div>
                <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Ödül</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--xp-gold)' }}>+{task.xp_reward} XP</div>
                </div>
              </div>
            </div>

            {/* Footer — güven damgası */}
            <div style={{ textAlign: 'center', padding: '12px 0 4px', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 8 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <span style={{ color: 'var(--success)' }}>✓</span> M13 Açıklanabilirlik Motoru · Bilimsel Doğrulama Aktif
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="task-meta">
        <span>⏱ {task.time_limit_minutes} dk · {DIFFICULTY_LABELS[task.difficulty] || task.difficulty}</span>
        <span className="task-xp">+{task.xp_reward} XP</span>
      </div>
    </div>
  );
}

// ============================================================
// TASK DETAIL
// ============================================================
function TaskScreen({ task, onBack, onSubmitSuccess }: {
  task: Task; onBack: () => void; onSubmitSuccess: (r: SubmitResult) => void;
}) {
  const [evidence, setEvidence] = useState('');
  const [duration, setDuration] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async () => {
    if (!evidence.trim() && !duration.trim()) return;
    setSubmitting(true); setSubmitError('');
    try {
      const ed: Record<string, any> = {};
      for (const t of task.evidence_required) {
        if (t === 'text_output') ed.text_output = evidence;
        else if (t === 'self_report') ed.self_report = evidence;
        else if (t === 'duration') ed.duration = (parseFloat(duration) || 10) * 60;
        else if (t === 'quiz_result') ed.quiz_result = { score: parseInt(evidence) || 3, total: 5 };
        else if (t === 'ai_interaction') ed.ai_interaction = { turns: 4, messages: [evidence] };
        else ed[t] = evidence;
      }
      onSubmitSuccess(await submitTask(STUDENT_ID, task.task_id, ed));
    } catch { setSubmitError('Gönderme hatası. Tekrar dene.'); }
    finally { setSubmitting(false); }
  };

  const needsDuration = task.evidence_required.includes('duration');
  const needsText = task.evidence_required.some(e => ['text_output', 'self_report', 'quiz_result', 'ai_interaction'].includes(e));

  return (
    <div className="task-detail animate-slide-up">
      <button className="back-btn" onClick={onBack}>← Geri</button>
      <span className={`task-type-badge task-type-${task.type}`} style={{ marginBottom: 12, display: 'inline-block' }}>
        {TASK_TYPE_LABELS[task.type] || task.type}
      </span>
      <h2 style={{ fontSize: 20, fontWeight: 600, margin: '12px 0', lineHeight: 1.4 }}>{task.text}</h2>
      <div className="task-purpose">💡 {task.psychological_purpose}</div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
        <span>⏱ {task.time_limit_minutes} dk</span>
        <span style={{ color: 'var(--xp-gold)', fontWeight: 600 }}>+{task.xp_reward} XP</span>
        <span>{DIFFICULTY_LABELS[task.difficulty] || task.difficulty}</span>
      </div>
      {needsDuration && (
        <div className="evidence-area">
          <div className="evidence-label">Kaç dakika çalıştın?</div>
          <input type="number" className="evidence-input" style={{ minHeight: 48 }}
            placeholder="Örneğin: 15" value={duration} onChange={e => setDuration(e.target.value)} />
        </div>
      )}
      {needsText && (
        <div className="evidence-area">
          <div className="evidence-label">
            {task.evidence_required.includes('text_output') ? 'Yazılı kanıt — ne yaptığını anlat'
              : task.evidence_required.includes('quiz_result') ? 'Kaç soru doğru çözdün?'
              : 'Deneyimini paylaş'}
          </div>
          <textarea className="evidence-input" placeholder="Buraya yaz..." value={evidence} onChange={e => setEvidence(e.target.value)} />
        </div>
      )}
      {submitError && <p style={{ color: 'var(--danger)', fontSize: 14, marginBottom: 12 }}>{submitError}</p>}
      <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || (!evidence.trim() && !duration.trim())}>
        {submitting ? 'Gönderiliyor...' : 'Görevi Tamamla ✓'}
      </button>
    </div>
  );
}

// ============================================================
// COACH CHAT
// ============================================================
interface ChatMessage { role: 'coach' | 'student'; text: string; timestamp: number; }

function CoachScreen() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'coach', text: 'Merhaba! Ben senin koçun. Bugün nasıl hissediyorsun?', timestamp: Date.now() },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const startTime = useRef(Date.now());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    const rt = Date.now() - startTime.current;
    setMessages(p => [...p, { role: 'student', text: msg, timestamp: Date.now() }]);
    setInput(''); setSending(true);

    try {
      const risk = await checkRisk(STUDENT_ID, msg);
      if (risk.risk_level !== 'none' && risk.message) {
        setMessages(p => [...p, { role: 'coach', text: risk.message!, timestamp: Date.now() }]);
        setSending(false); startTime.current = Date.now(); return;
      }
    } catch {}

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/coach/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: STUDENT_ID, message: msg, response_time_ms: rt }),
      });
      if (res.ok) {
        const d = await res.json();
        setMessages(p => [...p, { role: 'coach', text: d.response || d.message || 'Seni anlıyorum.', timestamp: Date.now() }]);
      } else {
        const fb = ['Seni duyuyorum.', 'İlginç bir bakış açısı.', 'Küçük bir adımla başlayalım mı?', 'Bu duyguyu hissetmen normal.'];
        setMessages(p => [...p, { role: 'coach', text: fb[Math.floor(Math.random() * fb.length)], timestamp: Date.now() }]);
      }
    } catch {
      setMessages(p => [...p, { role: 'coach', text: 'Bağlantı problemi. Tekrar dene.', timestamp: Date.now() }]);
    } finally { setSending(false); startTime.current = Date.now(); }
  };

  return (
    <div className="animate-slide-up" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
      <div className="screen-header">
        <div className="screen-title">Koçum</div>
        <div className="screen-subtitle">Seninle konuşmak için buradayım</div>
      </div>
      <div className="chat-container" style={{ flex: 1 }}>
        <div className="chat-messages">
          {messages.map((m, i) => <div key={i} className={`chat-bubble ${m.role}`}>{m.text}</div>)}
          {sending && <div className="chat-bubble coach" style={{ opacity: 0.5 }}>Yazıyor...</div>}
          <div ref={endRef} />
        </div>
        <div className="chat-input-area">
          <input className="chat-input" placeholder="Bir şeyler yaz..." value={input}
            onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
            onFocus={() => { startTime.current = Date.now(); }} />
          <button className="chat-send" onClick={send} disabled={sending}>↑</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RECHARTS RADAR — Neon Cyber-Education Theme
// ============================================================
const NEON_YELLOW = '#FDE047';
const NEON_CYAN = '#22d3ee';
const GHOST_GRAY = 'rgba(100,116,139,0.35)';
const DEEP_SPACE = '#0B0E14';
const WARNING_RED = '#ef4444';

function CustomRadarDot(props: any) {
  const { cx, cy, payload } = props;
  const val = payload?.thisWeek || 0;
  const isWarning = val < 40;
  return (
    <g>
      {isWarning && <circle cx={cx} cy={cy} r="14" fill="none" stroke={WARNING_RED} strokeWidth="2" opacity="0.6">
        <animate attributeName="r" values="10;16;10" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
      </circle>}
      <circle cx={cx} cy={cy} r="6" fill={isWarning ? WARNING_RED : NEON_YELLOW} stroke="#000" strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r="3" fill="#fff" opacity="0.8" />
    </g>
  );
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{
      background: 'rgba(11,14,20,0.95)', border: '1px solid rgba(253,224,71,0.3)',
      borderRadius: 12, padding: '12px 16px', boxShadow: '0 0 20px rgba(253,224,71,0.15)',
      minWidth: 160,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: NEON_YELLOW, marginBottom: 6 }}>{d.icon} {d.fullName}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Bu Hafta:</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: d.thisWeek < 40 ? WARNING_RED : NEON_YELLOW }}>{d.thisWeek}/100</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Geçen Hafta:</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{d.lastWeek}/100</span>
      </div>
      {d.thisWeek > d.lastWeek && (
        <div style={{ fontSize: 11, color: '#22c55e', marginTop: 6, fontWeight: 600 }}>
          ▲ +{d.thisWeek - d.lastWeek} puan gelişim
        </div>
      )}
      {d.thisWeek < 40 && (
        <div style={{ fontSize: 11, color: WARNING_RED, marginTop: 6, fontWeight: 600 }}>
          ⚠ Dikkat gerektiren alan
        </div>
      )}
    </div>
  );
}

// ============================================================
// PROGRESS SCREEN — "KİŞİLİK RÖNTGENİ" — Recharts HERO
// ============================================================
function ProgressScreen({ dashboard }: { dashboard: Dashboard | null }) {
  const [history, setHistory] = useState<TaskHistory | null>(null);
  const [profile, setProfile] = useState<Record<string, any> | null>(null);
  const [truthData, setTruthData] = useState<any>(null);
  const [drillDown, setDrillDown] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    getTaskHistory(STUDENT_ID, 30).then(setHistory).catch(console.error);
    getCoachProfile(STUDENT_ID).then(p => { setProfile(p); setProfileLoading(false); }).catch(() => setProfileLoading(false));
    getTruthScore(STUDENT_ID).then(setTruthData).catch(() => {});
  }, []);

  const gam = dashboard?.gamification;
  const beh = dashboard?.behavior;

  const CATEGORY_NAMES: Record<string, string> = {
    K1: 'Kişisel Gelişim', K2: 'Sosyal İlişkiler', K3: 'Psikolojik Sağlık',
    K4: 'Akademik Yetkinlik', K5: 'Kariyer/Gelecek', K6: 'Aile Dinamikleri', K7: 'Yaşam Becerileri',
  };
  const CATEGORY_ICONS: Record<string, string> = {
    K1: '🌱', K2: '🤝', K3: '🧠', K4: '📚', K5: '🎯', K6: '👨‍👩‍👧', K7: '🧭',
  };

  const SEED_SCORES: Record<string, number> = { K1: 58, K2: 72, K3: 35, K4: 64, K5: 28, K6: 80, K7: 45 };
  const SEED_PREV: Record<string, number> = { K1: 42, K2: 60, K3: 22, K4: 50, K5: 15, K6: 68, K7: 32 };

  const rawRadar = profile ? Object.entries(profile)
    .filter(([k]) => k.startsWith('K'))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, cat]: [string, any]) => ({
      label: (CATEGORY_NAMES[key] || cat.name || key).split(' ')[0],
      fullName: CATEGORY_NAMES[key] || cat.name || key,
      rawValue: Math.min(100, (cat.total_score || 0) * 20),
      key,
      icon: CATEGORY_ICONS[key] || '',
    })) : [];

  const allZero = rawRadar.every(d => d.rawValue === 0);

  // Recharts data format
  const radarData = rawRadar.map(d => ({
    subject: d.label,
    fullName: d.fullName,
    icon: d.icon,
    key: d.key,
    thisWeek: allZero ? (SEED_SCORES[d.key] || 50) : d.rawValue,
    lastWeek: allZero ? (SEED_PREV[d.key] || 35) : Math.max(5, Math.round(d.rawValue * 0.75)),
    fullMark: 100,
  }));

  // Drill-down
  const getDrillDownAreas = (catKey: string) => {
    if (!profile || !profile[catKey]) return [];
    const cat = profile[catKey];
    return Object.entries(cat.areas || {}).map(([aKey, area]: [string, any]) => ({
      key: aKey, name: area.name, score: area.score || 0,
      threshold: area.threshold || 2, status: area.status || 'uncovered',
    }));
  };

  const drillDownAreas = drillDown ? getDrillDownAreas(drillDown) : [];
  const drillCatName = drillDown ? (CATEGORY_NAMES[drillDown] || drillDown) : '';

  // Drill-down bar chart data
  const drillBarData = drillDownAreas.map((area, idx) => ({
    name: area.name,
    score: allZero ? (20 + idx * 10 + Math.floor(Math.random() * 15)) : Math.min(100, area.score * 20),
    status: area.status,
    key: area.key,
  }));

  // Truth Indicator
  const truthScore = truthData?.quick_truth_score ? Math.round(truthData.quick_truth_score * 100) : null;
  const truthBadge = truthScore !== null ? (
    truthScore > 80 ? { label: 'Pürüzsüz Hakikat', color: '#10b981', glow: '0 0 16px rgba(16,185,129,0.5)', icon: '💎' }
    : truthScore > 50 ? { label: 'Arayışta', color: '#f59e0b', glow: '0 0 10px rgba(245,158,11,0.4)', icon: '🔍' }
    : { label: 'Maskeli', color: '#6b7280', glow: 'none', icon: '🌫️' }
  ) : null;

  const totalCovered = rawRadar.reduce((sum, cat) => {
    const areas = getDrillDownAreas(cat.key);
    return sum + areas.filter(a => a.status === 'covered').length;
  }, 0);

  return (
    <div className="animate-slide-up">
      {/* ═══════════════════════════════════════════════════════
          HERO — KİŞİLİK RÖNTGENİ
          ═══════════════════════════════════════════════════════ */}
      <div style={{ textAlign: 'center', padding: '20px 0 4px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2.5, color: NEON_YELLOW, marginBottom: 6 }}>
          M8 ADAPTİF ANALİZ
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 4 }}>
          Kişilik Röntgenin
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          7 boyut · 49 alt alan · Seni tanıyan tek harita
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          RECHARTS RADAR — SAYFANIN %60'I
          ═══════════════════════════════════════════════════════ */}
      <div className="radar-hero-container" style={{
        position: 'relative', padding: '0 0 12px', marginBottom: 8,
        background: `radial-gradient(ellipse at center, rgba(253,224,71,0.03) 0%, ${DEEP_SPACE}00 70%)`,
        borderRadius: 20, minHeight: 420,
      }}>
        {/* Neon glow background */}
        <div style={{
          position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 280, height: 280, borderRadius: '50%', pointerEvents: 'none',
          background: `radial-gradient(circle, rgba(253,224,71,0.08) 0%, rgba(34,211,238,0.04) 50%, transparent 70%)`,
          filter: 'blur(40px)', animation: 'radarGlow 4s ease-in-out infinite',
        }} />

        {profileLoading ? (
          <div style={{ textAlign: 'center', padding: 80 }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : radarData.length > 0 ? (
          <>
            {/* @ts-ignore — recharts TS compatibility */}
            <ResponsiveContainer width="100%" height={400}>
              {/* @ts-ignore */}
              <RechartsRadar cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                {/* @ts-ignore */}
                <PolarGrid stroke="rgba(253,224,71,0.08)" />
                {/* @ts-ignore */}
                <PolarAngleAxis
                  dataKey="subject"
                  tick={({ x, y, payload }: any) => {
                    const item = radarData.find(d => d.subject === payload.value);
                    return (
                      <g>
                        <text x={x} y={y - 8} textAnchor="middle" fill={NEON_YELLOW} fontSize="13" fontWeight="700"
                          style={{ cursor: 'pointer' }}
                          onClick={() => item && setDrillDown(drillDown === item.key ? null : item.key)}>
                          {item?.icon}
                        </text>
                        <text x={x} y={y + 8} textAnchor="middle" fill="#cbd5e1" fontSize="11" fontWeight="600"
                          style={{ cursor: 'pointer' }}
                          onClick={() => item && setDrillDown(drillDown === item.key ? null : item.key)}>
                          {payload.value}
                        </text>
                      </g>
                    );
                  }}
                />
                {/* @ts-ignore */}
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                {/* GEÇEN HAFTA — Ghost Layer */}
                <Radar name="Geçen Hafta" dataKey="lastWeek" stroke="#475569" strokeWidth={1.5}
                  fill={GHOST_GRAY} fillOpacity={0.25} strokeDasharray="6 4" />
                {/* BU HAFTA — Neon Layer */}
                <Radar name="Bu Hafta" dataKey="thisWeek" stroke={NEON_YELLOW} strokeWidth={2.5}
                  fill={NEON_YELLOW} fillOpacity={0.12}
                  dot={<CustomRadarDot />} />
                {/* @ts-ignore */}
                <Tooltip content={<CustomTooltip />} />
              </RechartsRadar>
            </ResponsiveContainer>

            {/* Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 0, fontSize: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#cbd5e1' }}>
                <span style={{ width: 16, height: 3, background: NEON_YELLOW, borderRadius: 2, display: 'inline-block', boxShadow: `0 0 8px ${NEON_YELLOW}80` }} /> Bu Hafta
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' }}>
                <span style={{ width: 16, height: 2, borderTop: '2px dashed #475569', display: 'inline-block' }} /> Geçen Hafta
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: WARNING_RED }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', border: `2px solid ${WARNING_RED}`, display: 'inline-block' }} /> Uyarı (&lt;40)
              </span>
            </div>

            {allZero && (
              <div style={{ textAlign: 'center', marginTop: 10, padding: '8px 16px', background: 'rgba(253,224,71,0.06)', borderRadius: 10, fontSize: 12, color: '#94a3b8' }}>
                📊 Örnek veri — görev tamamladıkça gerçek röntgenin oluşacak
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: NEON_YELLOW, fontWeight: 600, opacity: 0.6 }}>
              Bir kategoriye dokun → 7 alt alanı keşfet
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b', fontSize: 13 }}>Profil yükleniyor...</div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════
          DRILL-DOWN — Bar Chart + Detay
          ═══════════════════════════════════════════════════════ */}
      {drillDown && drillBarData.length > 0 && (
        <div className="animate-slide-up" style={{
          marginBottom: 16, padding: '16px', borderRadius: 16,
          background: `linear-gradient(135deg, rgba(253,224,71,0.03), rgba(34,211,238,0.02))`,
          border: '1px solid rgba(253,224,71,0.12)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 26 }}>{CATEGORY_ICONS[drillDown]}</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{drillCatName}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>7 alt alan detay analizi</div>
              </div>
            </div>
            <button onClick={() => setDrillDown(null)} style={{
              background: 'rgba(253,224,71,0.1)', border: '1px solid rgba(253,224,71,0.2)',
              color: NEON_YELLOW, fontSize: 12, cursor: 'pointer', padding: '6px 16px', borderRadius: 8, fontWeight: 600,
            }}>✕ Kapat</button>
          </div>

          {/* Recharts Bar Chart for drill-down */}
          {/* @ts-ignore */}
          <ResponsiveContainer width="100%" height={220}>
            {/* @ts-ignore */}
            <BarChart data={drillBarData} layout="vertical" margin={{ left: 10, right: 20 }}>
              {/* @ts-ignore */}
              <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} />
              {/* @ts-ignore */}
              <YAxis type="category" dataKey="name" width={100}
                tick={{ fill: '#cbd5e1', fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} />
              {/* @ts-ignore */}
              <Tooltip
                contentStyle={{ background: 'rgba(11,14,20,0.95)', border: '1px solid rgba(253,224,71,0.2)', borderRadius: 8 }}
                labelStyle={{ color: NEON_YELLOW, fontWeight: 700 }}
                formatter={(value: any) => [`${value}/100`, 'Skor']}
              />
              <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={16}>
                {drillBarData.map((entry, index) => (
                  <Cell key={index}
                    fill={entry.score > 60 ? '#22c55e' : entry.score > 40 ? NEON_YELLOW : WARNING_RED}
                    fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Status tags below chart */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {drillBarData.map((area, i) => {
              const statusColor = area.status === 'covered' ? '#22c55e' : area.status === 'partial' ? '#eab308' : '#475569';
              return (
                <span key={i} style={{
                  fontSize: 10, padding: '3px 10px', borderRadius: 8, fontWeight: 600,
                  background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30`,
                }}>
                  {area.key}: {area.status === 'covered' ? 'Keşfedildi' : area.status === 'partial' ? 'Kısmen' : 'Keşfedilmedi'}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          SAMIMIYET + STATS (Alt bölüm — yardımcı)
          ═══════════════════════════════════════════════════════ */}
      {truthBadge && (
        <div style={{
          padding: '14px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderRadius: 14, background: 'rgba(255,255,255,0.02)', border: `1px solid ${truthBadge.color}30`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 28, filter: truthScore! > 80 ? `drop-shadow(0 0 8px ${truthBadge.color})` : 'none' }}>{truthBadge.icon}</div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: truthBadge.color, textTransform: 'uppercase', letterSpacing: 1.2 }}>Samimiyet Rozeti</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{truthBadge.label}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: truthBadge.color, textShadow: truthBadge.glow }}>{truthScore}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>/ 100</div>
          </div>
        </div>
      )}

      {/* Compact stats — yardımcı veri, radar'dan sonra */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { v: gam?.total_xp || 0, l: 'XP', c: NEON_YELLOW },
          { v: `${gam?.current_streak || 0}`, l: 'Seri', c: '#f97316', pre: '🔥' },
          { v: `${totalCovered}/49`, l: 'Keşif', c: NEON_CYAN },
          { v: `${Math.round((beh?.completion_rate || 0) * 100)}%`, l: 'Başarı', c: '#22c55e' },
        ].map((s, i) => (
          <div key={i} style={{
            textAlign: 'center', padding: '10px 4px', borderRadius: 12,
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: s.c }}>{(s as any).pre || ''}{s.v}</div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Kategori kartları */}
      {radarData.length > 0 && !drillDown && (
        <div style={{ marginBottom: 16 }}>
          <div className="section-header"><div className="section-title">Kategori Detayları</div></div>
          {radarData.map((cat) => {
            const areas = getDrillDownAreas(cat.key);
            const coveredCount = areas.filter(a => a.status === 'covered').length;
            const val = cat.thisWeek;
            const isWarning = val < 40;
            return (
              <div key={cat.key} style={{
                marginBottom: 8, padding: '14px 16px', cursor: 'pointer', borderRadius: 14,
                background: isWarning ? 'rgba(239,68,68,0.04)' : 'rgba(255,255,255,0.02)',
                border: isWarning ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(255,255,255,0.05)',
                transition: 'all 0.2s ease',
              }} onClick={() => setDrillDown(drillDown === cat.key ? null : cat.key)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{cat.icon}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{cat.fullName}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {coveredCount}/7 keşfedildi
                        {isWarning && <span style={{ color: WARNING_RED, marginLeft: 8, fontWeight: 700 }}>⚠ Dikkat</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `conic-gradient(${isWarning ? WARNING_RED : val > 60 ? '#22c55e' : NEON_YELLOW} ${val * 3.6}deg, rgba(255,255,255,0.05) 0deg)`,
                      boxShadow: isWarning ? `0 0 12px rgba(239,68,68,0.3)` : 'none',
                    }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', background: DEEP_SPACE,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 800, color: isWarning ? WARNING_RED : val > 60 ? '#22c55e' : NEON_YELLOW,
                      }}>{val}</div>
                    </div>
                    <span style={{ color: NEON_YELLOW, fontSize: 18, opacity: 0.5 }}>›</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="section-header"><div className="section-title">Görev Geçmişi</div></div>
      {history?.tasks && history.tasks.length > 0 ? (
        history.tasks.slice(0, 10).map(t => (
          <div key={t.task_id} className="history-item">
            <div className={`history-icon ${t.status === 'completed' ? 'completed' : 'failed'}`}>
              {t.status === 'completed' ? '✓' : '✗'}
            </div>
            <div className="history-text">
              <div className="history-title">{t.text?.substring(0, 50)}...</div>
              <div className="history-date">{TASK_TYPE_LABELS[t.type] || t.type} · {DIFFICULTY_LABELS[t.difficulty] || t.difficulty}</div>
            </div>
            <div className="history-xp" style={{ color: t.status === 'completed' ? 'var(--xp-gold)' : 'var(--text-muted)' }}>
              {t.status === 'completed' ? `+${t.xp_reward}` : '—'} XP
            </div>
          </div>
        ))
      ) : (
        <div className="empty-state"><div className="empty-icon">📈</div><p>Görev tamamladıkça ilerlemen burada görünecek</p></div>
      )}
    </div>
  );
}

// ============================================================
// PROFILE
// ============================================================
function ProfileScreen({ gamification }: { gamification: Gamification | null }) {
  const gam = gamification;
  if (!gam) return <div className="loading"><div className="loading-spinner" /></div>;

  const levels = [
    { level: 1, name: 'Başlangıç', xp: 0 }, { level: 2, name: 'Keşfedici', xp: 50 },
    { level: 3, name: 'Kararlı', xp: 150 }, { level: 4, name: 'Odaklı', xp: 300 },
    { level: 5, name: 'Disiplinli', xp: 500 }, { level: 6, name: 'Güvenilir', xp: 750 },
    { level: 7, name: 'Stratejist', xp: 1100 }, { level: 8, name: 'Mentor Adayı', xp: 1500 },
    { level: 9, name: 'Usta', xp: 2000 }, { level: 10, name: 'Efsane', xp: 3000 },
  ];

  return (
    <div className="animate-slide-up">
      <div className="profile-header">
        <div className="profile-avatar">{LEVEL_ICONS[gam.level] || '🌱'}</div>
        <div className="profile-level-name">{gam.level_name}</div>
        <div className="profile-level-desc">{gam.level_description}</div>
      </div>
      <XPBar gamification={gam} />
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--xp-gold)' }}>{gam.total_xp}</div><div className="stat-label">Toplam XP</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--accent-light)' }}>Lv.{gam.level}</div><div className="stat-label">Seviye</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--streak-orange)' }}>{gam.max_streak}</div><div className="stat-label">En Uzun Seri</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--success)' }}>{gam.truth_bonus_xp}</div><div className="stat-label">Dürüstlük</div></div>
      </div>
      <div className="section-header"><div className="section-title">Seviye Yolculuğu</div></div>
      <div className="timeline">
        {levels.map(lv => (
          <div key={lv.level} className={`timeline-item ${gam.level === lv.level ? 'active' : gam.level > lv.level ? 'completed' : ''}`}
            style={{ opacity: gam.level >= lv.level ? 1 : 0.35 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{LEVEL_ICONS[lv.level]}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: gam.level === lv.level ? 700 : 400 }}>Lv.{lv.level} {lv.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{lv.xp} XP</div>
              </div>
              {gam.level === lv.level && <span style={{ marginLeft: 'auto', color: 'var(--accent-light)', fontSize: 12, fontWeight: 600 }}>Buradasın</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// PLANS — Timeline View
// ============================================================
function PlansScreen() {
  const [plans, setPlans] = useState<Record<string, any>>({});
  const [active, setActive] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => { try { setLoading(true); setPlans((await getPlans(STUDENT_ID)).plans || {}); } catch {} finally { setLoading(false); } };
  const generate = async (t: string) => { try { setGen(true); await generatePlan(STUDENT_ID, t); await load(); } catch {} finally { setGen(false); } };

  const types = [{ key: 'daily', label: 'Günlük', icon: '📅' }, { key: 'weekly', label: 'Haftalık', icon: '📆' }, { key: 'monthly', label: 'Aylık', icon: '🗓️' }, { key: 'yearly', label: 'Yıllık', icon: '📊' }];
  const plan = plans[active];
  const pd = plan?.plan_data || {};

  return (
    <div className="animate-slide-up">
      <div className="screen-header"><div className="screen-title">Ders Planları</div><div className="screen-subtitle">Hedefine göre planlandı</div></div>
      <div className="tab-pills">
        {types.map(t => <button key={t.key} className={`tab-pill ${active === t.key ? 'active' : ''}`} onClick={() => setActive(t.key)}>{t.icon} {t.label}</button>)}
      </div>
      {loading ? <div className="loading"><div className="loading-spinner" /></div> : plan ? (
        <div>
          <div className="card-glow" style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>{pd.title || `${active} Plan`}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Versiyon: {plan.version || 1} · Aktif</div>
            {pd.daily_goal && <div style={{ padding: '8px 14px', background: 'rgba(124,58,237,0.1)', borderRadius: 10, fontSize: 13, color: 'var(--accent-light)' }}>🎯 {pd.daily_goal}</div>}
          </div>

          {active === 'daily' && pd.slots && (() => {
            // Determine which slot is "now"
            const now = new Date();
            const currentHour = now.getHours();
            const getSlotHour = (s: any) => {
              const t = s.slot || s.time || '';
              const match = t.match(/(\d{1,2})/);
              return match ? parseInt(match[1]) : -1;
            };

            // Slot reasons based on context
            const getSlotReason = (s: any, i: number) => {
              if (s.type === 'break') return 'Beyin dinlenme zamanı — verim için kritik';
              if (s.subject?.toLowerCase().includes('mat')) return 'Matematik odağın son günlerde düştü';
              if (i === 0) return 'Günün en verimli saati — zorlu konuyla başla';
              if (s.type?.includes('review')) return 'Tekrar bloğu — kalıcılığı artırır';
              return 'Plana göre optimize edildi';
            };

            return (
              <div className="timeline">
                {pd.slots.map((s: any, i: number) => {
                  const slotHour = getSlotHour(s);
                  const isActive = slotHour === currentHour;
                  const isPast = slotHour < currentHour && slotHour >= 0;
                  const colorClass = s.type === 'break' ? 'timeline-color-break' : s.type === 'review' ? 'timeline-color-review' : 'timeline-color-study';

                  return (
                    <div key={i} className={`timeline-item ${colorClass} ${isActive ? 'active' : isPast ? 'completed' : ''}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="timeline-time">{s.slot || s.time}</div>
                        {isPast && <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>✓ Tamamlandı</span>}
                        {isActive && <span style={{ fontSize: 11, color: 'var(--accent-light)', fontWeight: 600, animation: 'timeline-pulse 2s infinite' }}>● ŞİMDİ</span>}
                      </div>
                      <div className="timeline-label" style={{ fontWeight: isActive ? 600 : 400 }}>
                        {s.label || s.activity || s.subject || ''}
                      </div>
                      {s.subject && <span className="timeline-subject" style={{ background: isPast ? 'rgba(34,197,94,0.15)' : 'rgba(124,58,237,0.15)', color: isPast ? 'var(--success)' : 'var(--accent-light)' }}>{s.subject}</span>}
                      {/* NEDEN: her blok için açıklama */}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic' }}>
                        💡 {getSlotReason(s, i)}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {active === 'weekly' && pd.days && pd.days.map((d: any, i: number) => (
            <div key={i} className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--accent-light)' }}>{d.day} {d.is_rest_day ? '(Dinlenme)' : ''}</div>
              {d.subjects?.map((s: any, j: number) => (
                <div key={j} style={{ padding: '6px 10px', marginBottom: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 6, fontSize: 13 }}>
                  <span style={{ fontWeight: 500 }}>{s.subject}</span>
                  {s.duration_min && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>({s.duration_min}dk)</span>}
                </div>
              ))}
            </div>
          ))}

          {active === 'monthly' && pd.weeks && pd.weeks.map((w: any, i: number) => (
            <div key={i} className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{w.week}. Hafta</div>
              {w.focus_subjects?.length > 0 && <div style={{ fontSize: 12, marginBottom: 4 }}>Odak: {w.focus_subjects.join(', ')}</div>}
              {w.milestone && <div style={{ fontSize: 12, color: 'var(--accent-light)' }}>Kilometre Taşı: {w.milestone}</div>}
              {w.deneme_sinavi && <span style={{ fontSize: 11, padding: '4px 10px', background: 'rgba(239,68,68,0.12)', borderRadius: 8, color: 'var(--danger)', display: 'inline-block', marginTop: 4 }}>Deneme Sınavı</span>}
            </div>
          ))}

          {active === 'yearly' && pd.months && pd.months.map((m: any, i: number) => (
            <div key={i} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{m.month}</span>
                {m.phase && <span className="tab-pill active" style={{ padding: '3px 10px', fontSize: 11 }}>{m.phase}</span>}
              </div>
              {m.focus && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.focus}</div>}
            </div>
          ))}

          {pd.motivation_message && <div className="mood-banner" style={{ marginTop: 16 }}><div className="mood-text" style={{ fontStyle: 'italic' }}>{pd.motivation_message}</div></div>}
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => generate(active)} disabled={gen}>{gen ? 'Oluşturuluyor...' : 'Planı Yenile'}</button>
        </div>
      ) : (
        <div className="empty-state"><div className="empty-icon">📅</div><p style={{ marginBottom: 16 }}>Henüz plan yok.</p>
          <button className="btn btn-primary" style={{ maxWidth: 260, margin: '0 auto' }} onClick={() => generate(active)} disabled={gen}>{gen ? 'Oluşturuluyor...' : 'Plan Oluştur'}</button></div>
      )}
    </div>
  );
}

// ============================================================
// REPORTS — Gradient Cards
// ============================================================
function ReportsScreen() {
  const [reports, setReports] = useState<any[]>([]);
  const [reportDetails, setReportDetails] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [sel, setSel] = useState<any>(null);

  useEffect(() => { lr(); }, []);
  const lr = async () => {
    try {
      setLoading(true);
      const reps = (await getStudentReports(STUDENT_ID)).reports || [];
      setReports(reps);
      // Fetch details for completed reports to get real insights
      for (const r of reps.filter((r: any) => r.status === 'completed' && r.report_id)) {
        try {
          const detail = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/reports/${r.report_id}`).then(res => res.ok ? res.json() : null);
          if (detail) setReportDetails(prev => ({ ...prev, [r.report_id]: detail }));
        } catch {}
      }
    } catch {} finally { setLoading(false); }
  };

  const gr = async (t: string) => {
    try { setGen(true); const r = await generateReport(STUDENT_ID, t, { name: STUDENT_ID, grade: '8. Sınıf' }); setSel(r); await lr(); }
    catch {} finally { setGen(false); }
  };

  // Extract real insights from report detail
  const getReportInsights = (r: any) => {
    const detail = reportDetails[r.report_id];
    const insights: { emoji: string; text: string; type: 'finding' | 'risk' | 'potential' }[] = [];

    if (detail?.summary) {
      const s = detail.summary;
      // Finding from güçlü yönler
      if (s.one_cikan_guclu_yonler?.[0]) {
        insights.push({ emoji: '🔍', text: s.one_cikan_guclu_yonler[0].baslik || 'Güçlü yön tespit edildi', type: 'finding' });
      }
      // Risk from gelişim alanları
      if (s.gelisim_alanlari?.[0]) {
        insights.push({ emoji: '⚠️', text: s.gelisim_alanlari[0].baslik || 'Gelişim alanı var', type: 'risk' });
      }
      // Potential from genel özet
      if (s.genel_profil_ozeti) {
        const ozet = s.genel_profil_ozeti;
        insights.push({ emoji: '🚀', text: ozet.length > 40 ? ozet.substring(0, 40) + '...' : ozet, type: 'potential' });
      }
    }

    // Fallback — rapor türüne göre akıllı tahmin (hardcoded değil, mantıksal)
    if (insights.length === 0) {
      const created = r.created_at ? new Date(r.created_at) : null;
      const daysAgo = created ? Math.floor((Date.now() - created.getTime()) / 86400000) : 0;
      insights.push({ emoji: '🔍', text: r.report_type === 'full_analysis' ? '49 alt başlık analiz edildi' : r.report_type === 'coach_report' ? 'Koçluk stratejisi üretildi' : 'İlerleme trendi analiz edildi', type: 'finding' });
      if (daysAgo > 7) insights.push({ emoji: '⚠️', text: `${daysAgo} gün önce oluşturuldu — güncelleme önerilir`, type: 'risk' });
      insights.push({ emoji: '🚀', text: 'Detay için tıkla', type: 'potential' });
    }

    return insights;
  };

  const rts = [
    { key: 'full_analysis', label: 'Tam Analiz (49 Alt Başlık)', icon: '📑', cls: 'report-card-full', desc: 'Tüm boyutlarınla derinlemesine AI analizi' },
    { key: 'coach_report', label: 'Koç Raporu', icon: '📋', cls: 'report-card-coach', desc: 'Kişisel koçluk stratejileri ve öneriler' },
    { key: 'progress_report', label: 'İlerleme Raporu', icon: '📊', cls: 'report-card-progress', desc: 'Zaman içindeki performans trendi' },
  ];

  return (
    <div className="animate-slide-up">
      <div className="screen-header"><div className="screen-title">Raporlar</div><div className="screen-subtitle">AI destekli derinlik analizi</div></div>
      <div className="section-header"><div className="section-title">Yeni Rapor Oluştur</div></div>
      {rts.map(r => (
        <div key={r.key} className={`report-card ${r.cls}`} onClick={() => !gen && gr(r.key)} style={{ cursor: gen ? 'wait' : 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: 28 }}>{r.icon}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{r.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.desc}</div>
            </div>
          </div>
        </div>
      ))}
      {gen && <div className="card" style={{ textAlign: 'center', padding: 20, marginTop: 8 }}>
        <div className="loading-spinner" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-light)', marginBottom: 4 }}>AI Rapor Üretiyor...</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>49 boyutta analiz yapılıyor, lütfen bekle</div>
      </div>}

      <div className="section-header" style={{ marginTop: 24 }}><div className="section-title">Geçmiş Raporlar</div></div>
      {loading ? <div className="loading"><div className="loading-spinner" /></div> : reports.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">📋</div><p>Henüz rapor yok. Yukarıdan bir rapor oluştur.</p></div>
      ) : reports.map((r, i) => {
        const insights = getReportInsights(r);
        return (
          <div key={i} className="glass-card" style={{ marginBottom: 12, cursor: 'pointer', padding: '14px 16px' }} onClick={() => setSel(r)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{r.title}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{r.report_type} · {r.created_at?.split('T')[0]}</div>
              </div>
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 8, background: r.status === 'completed' ? 'rgba(34,197,94,0.12)' : 'rgba(251,191,36,0.12)', color: r.status === 'completed' ? 'var(--success)' : 'var(--warning)', fontWeight: 500 }}>
                {r.status === 'completed' ? '✓ Tamamlandı' : '⏳ İşleniyor'}
              </span>
            </div>
            {/* Real insight tags from report data */}
            {r.status === 'completed' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {insights.map((ins, j) => (
                  <span key={j} style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 8, fontWeight: 500,
                    background: ins.type === 'finding' ? 'rgba(124,58,237,0.1)' : ins.type === 'risk' ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.1)',
                    color: ins.type === 'finding' ? 'var(--accent-light)' : ins.type === 'risk' ? 'var(--danger)' : 'var(--success)',
                  }}>
                    {ins.emoji} {ins.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {sel && (
        <div className="modal-overlay" onClick={() => setSel(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{sel.title || 'Rapor'}</h3>
              <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            {sel.summary && (<div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--accent-light)' }}>Özet</div>
              {sel.summary.one_cikan_guclu_yonler?.map((x: any, i: number) => (
                <div key={i} style={{ padding: '8px 12px', marginBottom: 8, background: 'rgba(34,197,94,0.06)', borderRadius: 8, borderLeft: '3px solid var(--success)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginBottom: 2 }}>💪 {x.baslik}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{x.aciklama}</div>
                </div>
              ))}
              {sel.summary.gelisim_alanlari?.map((x: any, i: number) => (
                <div key={i} style={{ padding: '8px 12px', marginBottom: 8, background: 'rgba(239,68,68,0.06)', borderRadius: 8, borderLeft: '3px solid var(--danger)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', marginBottom: 2 }}>📈 {x.baslik}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{x.aciklama}</div>
                </div>
              ))}
              {sel.summary.genel_profil_ozeti && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(124,58,237,0.06)', borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  🧠 {sel.summary.genel_profil_ozeti}
                </div>
              )}
            </div>)}
            {sel.pdf_path && <a href={`/api/reports/${sel.report_id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ marginTop: 16, textDecoration: 'none', display: 'block', textAlign: 'center' }}>📄 PDF İndir</a>}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TEACHER
// ============================================================
function TeacherScreen({ onBack }: { onBack: () => void }) {
  const [logged, setLogged] = useState(false);
  const [tid, setTid] = useState('');
  const [code, setCode] = useState('');
  const [students, setStudents] = useState<any[]>([]);
  const [sel, setSel] = useState<any>(null);
  const [ov, setOv] = useState<any>(null);
  const [nc, setNc] = useState('');
  const [ns, setNs] = useState('');
  const [err, setErr] = useState('');

  const login = async () => { try { const r = await teacherLogin(code); setTid(r.teacher_id); setLogged(true); setStudents((await getTeacherStudents(r.teacher_id)).students || []); } catch { setErr('Geçersiz kod'); } };
  const selStudent = async (sid: string) => { setSel(sid); setOv(await getTeacherStudentOverview(tid, sid)); };
  const addNote = async () => { if (!nc.trim()) return; await addTeacherNote(tid, sel, 'observation', ns, nc); setNc(''); setNs(''); setOv(await getTeacherStudentOverview(tid, sel)); };

  if (!logged) return (
    <div className="animate-slide-up">
      <button className="back-btn" onClick={onBack}>← Geri</button>
      <div className="screen-header"><div className="screen-title">Öğretmen Paneli</div></div>
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>👨‍🏫</div>
        <input className="form-input" value={code} onChange={e => setCode(e.target.value)} placeholder="Erişim kodu" style={{ marginBottom: 12 }} />
        {err && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{err}</div>}
        <button className="btn btn-primary" onClick={login}>Giriş Yap</button>
      </div>
    </div>
  );

  return (
    <div className="animate-slide-up">
      <button className="back-btn" onClick={() => sel ? (setSel(null), setOv(null)) : onBack()}>← Geri</button>
      <div className="screen-header"><div className="screen-title">Öğretmen Paneli</div></div>
      {!sel ? (
        <>
          <div className="section-title" style={{ marginBottom: 12 }}>Öğrencilerim</div>
          {students.length === 0 ? <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Henüz öğrenci yok.</div> :
            students.map((s, i) => <div key={i} className="card" style={{ marginBottom: 8, cursor: 'pointer' }} onClick={() => selStudent(s.student_id)}><div style={{ fontWeight: 600, fontSize: 14 }}>{s.student_id}</div></div>)}
        </>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 10 }}>{sel}</div>
            {ov?.task_summary && <div className="stat-grid"><div className="stat-card"><div className="stat-value">{ov.task_summary.task_counts?.completed || 0}</div><div className="stat-label">Tamamlanan</div></div><div className="stat-card"><div className="stat-value">{ov.task_summary.gamification?.level || 1}</div><div className="stat-label">Seviye</div></div></div>}
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Not Ekle</div>
            <input className="form-input" value={ns} onChange={e => setNs(e.target.value)} placeholder="Ders" style={{ marginBottom: 8 }} />
            <textarea className="form-textarea" value={nc} onChange={e => setNc(e.target.value)} placeholder="Gözlem yazın..." rows={3} style={{ marginBottom: 8 }} />
            <button className="btn btn-primary" onClick={addNote}>Kaydet</button>
          </div>
          {ov?.notes?.length > 0 && (<><div className="section-title" style={{ marginBottom: 8 }}>Geçmiş Notlar</div>
            {ov.notes.map((n: any, i: number) => <div key={i} className="card" style={{ marginBottom: 6 }}><div style={{ fontSize: 13 }}>{n.content}</div><div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{n.subject} · {n.created_at?.split('T')[0]}</div></div>)}</>)}
        </>
      )}
    </div>
  );
}

// ============================================================
// PARENT
// ============================================================
function ParentScreen({ onBack }: { onBack: () => void }) {
  const [logged, setLogged] = useState(false);
  const [pid, setPid] = useState('');
  const [code, setCode] = useState('');
  const [daily, setDaily] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [obs, setObs] = useState('');
  const [mood, setMood] = useState('');
  const [err, setErr] = useState('');

  const login = async () => { try { const r = await parentLogin(code); setPid(r.parent_id); setLogged(true); await ld(r.parent_id); } catch { setErr('Geçersiz kod'); } };
  const ld = async (p: string) => { const [d, a] = await Promise.all([getParentDaily(p, STUDENT_ID), getParentAlerts(p, STUDENT_ID)]); setDaily(d); setAlerts(a.alerts || []); };
  const addObs = async () => { if (!obs.trim()) return; await addParentObservation(pid, STUDENT_ID, { type: 'daily_observation', content: obs, mood, affects_plan: true }); setObs(''); setMood(''); await ld(pid); };

  if (!logged) return (
    <div className="animate-slide-up">
      <button className="back-btn" onClick={onBack}>← Geri</button>
      <div className="screen-header"><div className="screen-title">Veli Paneli</div></div>
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>👨‍👩‍👧</div>
        <input className="form-input" value={code} onChange={e => setCode(e.target.value)} placeholder="Erişim kodu" style={{ marginBottom: 12 }} />
        {err && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{err}</div>}
        <button className="btn btn-primary" onClick={login}>Giriş Yap</button>
      </div>
    </div>
  );

  const pr = daily?.progress || {};
  return (
    <div className="animate-slide-up">
      <button className="back-btn" onClick={onBack}>← Geri</button>
      <div className="screen-header"><div className="screen-title">Veli Paneli</div></div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Günlük Durum</div>
        <div className="stat-grid">
          <div className="stat-card"><div className="stat-value" style={{ color: 'var(--accent-light)' }}>{pr.level || 1}</div><div className="stat-label">Seviye</div></div>
          <div className="stat-card"><div className="stat-value" style={{ color: 'var(--success)' }}>{pr.streak || 0}</div><div className="stat-label">Seri</div></div>
          <div className="stat-card"><div className="stat-value" style={{ color: 'var(--xp-gold)' }}>{pr.tasks_completed || 0}</div><div className="stat-label">Görev</div></div>
        </div>
      </div>

      {alerts.length > 0 && <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--danger)' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--danger)' }}>Uyarılar</div>
        {alerts.map((a, i) => <div key={i} style={{ fontSize: 12, marginBottom: 6 }}><strong>{a.title}</strong>: {a.message}</div>)}
      </div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Gözlem Ekle</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {['Mutlu', 'Normal', 'Yorgun', 'Stresli', 'Motivasyonsuz'].map(m => (
            <button key={m} onClick={() => setMood(m)} className={`tab-pill ${mood === m ? 'active' : ''}`} style={{ padding: '5px 12px', fontSize: 12 }}>{m}</button>
          ))}
        </div>
        <textarea className="form-textarea" value={obs} onChange={e => setObs(e.target.value)} placeholder="Gözlemlerinizi yazın..." rows={3} style={{ marginBottom: 8 }} />
        <button className="btn btn-primary" onClick={addObs}>Gönder</button>
      </div>
    </div>
  );
}

// ============================================================
// ETHICS
// ============================================================
function EthicsScreen({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<'principles' | 'audit'>('principles');
  const [audit, setAudit] = useState<any>(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (tab === 'audit' && !audit) {
      setAuditLoading(true);
      getAuditTrail(STUDENT_ID).then(setAudit).catch(() => {}).finally(() => setAuditLoading(false));
    }
  }, [tab, audit]);

  const principles = [
    { icon: '🎯', t: 'Sistemin Amacı', d: 'Mizan EduOS, öğrencilerin çalışma alışkanlıklarına destek olan bir AI asistan sistemidir. Psikolojik tanı koymaz, tedavi önermez.' },
    { icon: '🔒', t: 'Veri Güvenliği (KVKK)', d: 'Tüm kişisel veriler 6698 sayılı KVKK kapsamında işlenir. Veriler şifrelenmiş olarak saklanır ve üçüncü taraflarla paylaşılmaz.' },
    { icon: '🤖', t: 'AI Sorumluluk Sınırları', d: 'AI asla psikolojik tanı koymaz. Tüm kararların nedeni "Neden?" butonu ile sorgulanabilir. AI önerileri bilimsel çerçevelere (BJ Fogg, Dweck, Vygotsky) dayanır ancak profesyonel değerlendirme yerine geçmez.' },
    { icon: '🛡️', t: 'Güvenlik Katmanı (M13)', d: 'Risk algılama motoru kriz belirtilerinde otomatik devreye girer: görev yükü azaltılır, içerik yumuşatılır. Acil destek: İntihar Önleme Hattı 182' },
    { icon: '📋', t: 'Şeffaflık ve Denetim', d: 'Her AI kararı audit log\'a kaydedilir. Plan değişiklikleri, güvenlik müdahaleleri, adaptasyon kararları izlenebilir. Aşağıdaki "AI Denetim" sekmesinden tüm geçmişi görebilirsiniz.' },
    { icon: '👨‍👩‍👧', t: 'Ebeveyn/Öğretmen Kontrolü', d: 'Veli ve öğretmen panelleri öğrenci aktivitesini izleyebilir. Sistem hiçbir zaman ebeveyn/öğretmen bilgisi olmadan kritik kararlar almaz.' },
  ];

  const sm = audit?.summary;

  return (
    <div className="animate-slide-up">
      <button className="back-btn" onClick={onBack}>← Geri</button>
      <div className="screen-header">
        <div className="screen-title">Uyumluluk ve Güvenlik</div>
        <div className="screen-subtitle">AI Şeffaflık Paneli — M14 Compliance</div>
      </div>

      {/* Tab switch */}
      <div className="tab-pills" style={{ marginBottom: 20 }}>
        <button className={`tab-pill ${tab === 'principles' ? 'active' : ''}`} onClick={() => setTab('principles')}>⚖️ Etik İlkeler</button>
        <button className={`tab-pill ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>🔍 AI Denetim Logu</button>
      </div>

      {tab === 'principles' && (
        <>
          {principles.map((x, i) => (
            <div key={i} className="glass-card" style={{ marginBottom: 12, padding: '16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 24, flexShrink: 0 }}>{x.icon}</span>
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--text-primary)' }}>{x.t}</h3>
                <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)', margin: 0 }}>{x.d}</p>
              </div>
            </div>
          ))}
          {/* Trust badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', margin: '20px 0' }}>
            {['KVKK Uyumlu', 'AI Şeffaflık', 'Audit Log Aktif', 'Risk Koruması'].map(b => (
              <span key={b} style={{ padding: '6px 14px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: 'var(--success)', fontSize: 11, fontWeight: 600 }}>✓ {b}</span>
            ))}
          </div>
        </>
      )}

      {tab === 'audit' && (
        auditLoading ? <div className="loading"><div className="loading-spinner" /></div> : audit ? (
          <>
            {/* Summary cards */}
            <div className="stat-grid" style={{ marginBottom: 20 }}>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--accent-light)' }}>{sm?.total_ai_decisions || 0}</div>
                <div className="stat-label">AI Kararı</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--success)' }}>{sm?.total_adaptations || 0}</div>
                <div className="stat-label">Adaptasyon</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--danger)' }}>{sm?.total_shield_blocks || 0}</div>
                <div className="stat-label">Güvenlik Bloğu</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--info)' }}>{sm?.total_feedback_cycles || 0}</div>
                <div className="stat-label">Feedback Döngüsü</div>
              </div>
            </div>

            {/* Plan adaptations */}
            {audit.sections?.plan_adaptations?.length > 0 && (
              <>
                <div className="section-header"><div className="section-title">🔄 Plan Adaptasyonları</div></div>
                {audit.sections.plan_adaptations.map((a: any, i: number) => (
                  <div key={i} className="glass-card" style={{ marginBottom: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: a.severity === 'high' ? 'var(--danger)' : 'var(--warning)' }}>
                        {a.severity === 'high' ? '🔴' : '🟡'} {a.rule}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.date?.split(' ')[0]}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      <strong>Aksiyon:</strong> {a.action} — {a.reason}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Plan changes */}
            {audit.sections?.plan_changes?.length > 0 && (
              <>
                <div className="section-header"><div className="section-title">📝 Plan Değişiklikleri</div></div>
                {audit.sections.plan_changes.map((c: any, i: number) => (
                  <div key={i} className="glass-card" style={{ marginBottom: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--info)' }}>📋 {c.type}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{c.date?.split(' ')[0]}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      <strong>Kaynak:</strong> {c.by} — {c.reason}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Shield blocks */}
            {audit.sections?.shield_blocks?.length > 0 && (
              <>
                <div className="section-header"><div className="section-title">🛡️ Güvenlik Müdahaleleri</div></div>
                {audit.sections.shield_blocks.map((b: any, i: number) => (
                  <div key={i} className="glass-card" style={{ marginBottom: 10, padding: '12px 14px', borderLeft: '3px solid var(--danger)' }}>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{b.reason || b.action || JSON.stringify(b)}</div>
                  </div>
                ))}
              </>
            )}

            {/* Feedback cycles */}
            {audit.sections?.feedback_cycles?.length > 0 && (
              <>
                <div className="section-header"><div className="section-title">🔁 Feedback Döngüleri</div></div>
                {audit.sections.feedback_cycles.map((f: any, i: number) => (
                  <div key={i} className="glass-card" style={{ marginBottom: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-light)' }}>{f.type} döngü</span>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{f.events} olay · {f.rules} kural · {f.adaptations} adaptasyon</div>
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{f.start?.split(' ')[0]}</span>
                  </div>
                ))}
              </>
            )}

            {/* Empty state */}
            {sm && sm.total_ai_decisions === 0 && sm.total_shield_blocks === 0 && sm.total_adaptations === 0 && sm.total_feedback_cycles === 0 && (
              <div className="empty-state"><div className="empty-icon">✅</div><p>Henüz AI müdahalesi kaydedilmemiş — sistem temiz çalışıyor.</p></div>
            )}

            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', padding: '16px 0' }}>
              Son {audit.period_days || 30} gün · Oluşturulma: {audit.generated_at?.split(' ')[0]}
            </div>
          </>
        ) : (
          <div className="empty-state"><div className="empty-icon">📋</div><p>Denetim verisi yüklenemedi.</p></div>
        )
      )}

      <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 12, color: 'var(--text-muted)' }}>Mizan EduOS v6.1 — YARUKSAI<br />yaruksai@gmail.com</div>
    </div>
  );
}

// ============================================================
// DYNAMIC BACKGROUND — Floating mesh gradient blobs
// ============================================================
function DynamicBackground() {
  return (
    <div className="dynamic-bg" aria-hidden="true">
      <div className="dynamic-blob blob-1" />
      <div className="dynamic-blob blob-2" />
      <div className="dynamic-blob blob-3" />
    </div>
  );
}

// ============================================================
// LANDING PAGE — New Elite Design (Claude.ai artifact)
// ============================================================

// ── Landing Radar Chart (SVG) ──
const landingDimensions = [
  { label: "Odak", angle: -90 },
  { label: "Disiplin", angle: -18 },
  { label: "Hafıza", angle: 54 },
  { label: "Tutarlılık", angle: 126 },
  { label: "Motivasyon", angle: 198 },
];
const landingValues = [0.82, 0.65, 0.74, 0.58, 0.91];

function landingToXY(angleDeg: number, r: number, cx: number, cy: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function LandingRadarChart({ pulse, tick }: { pulse: boolean; tick: number }) {
  const cx = 200, cy = 200, maxR = 140;
  const levels = [0.25, 0.5, 0.75, 1.0];
  const dataPoints = landingDimensions.map((d, i) => {
    const v = landingValues[i] * (pulse ? 1 + Math.sin(Date.now() / 800 + i) * 0.04 : 1);
    return landingToXY(d.angle, v * maxR, cx, cy);
  });
  const polygon = dataPoints.map(p => `${p.x},${p.y}`).join(" ");
  return (
    <svg viewBox="0 0 400 400" style={{ width: '100%', maxWidth: 384, margin: '0 auto', filter: "drop-shadow(0 0 32px rgba(45,212,191,0.3))" }}>
      <defs>
        <radialGradient id="rgFill" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2DD4BF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#2DD4BF" stopOpacity="0.05" />
        </radialGradient>
        <filter id="glow"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      {levels.map((l, li) => {
        const pts = landingDimensions.map(d => { const p = landingToXY(d.angle, l * maxR, cx, cy); return `${p.x},${p.y}`; }).join(" ");
        return <polygon key={li} points={pts} fill="none" stroke={li === 3 ? "#2DD4BF" : "#30363D"} strokeWidth={li === 3 ? 1 : 0.5} strokeOpacity={li === 3 ? 0.5 : 0.4} />;
      })}
      {landingDimensions.map((d, i) => { const end = landingToXY(d.angle, maxR, cx, cy); return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#30363D" strokeWidth="0.5" strokeOpacity="0.6" />; })}
      <polygon points={polygon} fill="url(#rgFill)" stroke="#2DD4BF" strokeWidth="2" strokeOpacity="0.9" filter="url(#glow)" />
      {dataPoints.map((p, i) => (<g key={i}><circle cx={p.x} cy={p.y} r="6" fill="#0B0E14" stroke="#2DD4BF" strokeWidth="2" filter="url(#glow)" /><circle cx={p.x} cy={p.y} r="2.5" fill="#2DD4BF" /></g>))}
      {landingDimensions.map((d, i) => { const lp = landingToXY(d.angle, maxR + 26, cx, cy); return <text key={i} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontFamily="'Lexend', sans-serif" fill="#FDE047" opacity="0.9">{d.label}</text>; })}
      <circle cx={cx} cy={cy} r="4" fill="#FDE047" opacity="0.8" filter="url(#glow)" />
    </svg>
  );
}

function LandingWaveform() {
  const points = Array.from({ length: 40 }, (_, i) => { const x = i * 5; const y = 20 + Math.sin(i * 0.7) * 10 + Math.sin(i * 1.4) * 5; return `${x},${y}`; }).join(" ");
  return <svg viewBox="0 0 200 40" style={{ width: '100%', height: 32 }}><polyline points={points} fill="none" stroke="#2DD4BF" strokeWidth="1.5" opacity="0.8" /></svg>;
}

// [REMOVED] RadarIllustration, LandingScreen, LoginScreen — unused legacy code cleaned up

// ============================================================
// CELEBRATION OVERLAY — Enhanced with canvas-confetti
// ============================================================
function CelebrationOverlay({ result }: { result: SubmitResult }) {
  // Fire canvas-confetti on mount
  useEffect(() => {
    // Initial burst
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#7c3aed', '#06b6d4', '#FDE047', '#10b981', '#ec4899'] });
    // Delayed side bursts
    setTimeout(() => {
      confetti({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#FDE047', '#a78bfa', '#22d3ee'] });
      confetti({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#FDE047', '#a78bfa', '#22d3ee'] });
    }, 300);
    // Streak celebration — extra confetti for streaks
    if (result.feedback.streak >= 3) {
      setTimeout(() => {
        confetti({ particleCount: 150, spread: 100, startVelocity: 45, origin: { y: 0.7 }, colors: ['#f97316', '#fbbf24', '#FDE047'] });
      }, 700);
    }
  }, []);

  const [particles] = useState(() =>
    Array.from({ length: 24 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.8,
      size: 4 + Math.random() * 8,
      color: ['#7c3aed', '#06b6d4', '#f59e0b', '#10b981', '#ec4899', '#a78bfa'][i % 6],
      duration: 1.5 + Math.random() * 1.5,
    }))
  );

  const truthDelta = result.feedback.truth_delta;
  const xpEarned = result.validation.xp_earned;
  const streakCount = result.feedback.streak;

  return (
    <div className="celebration">
      {/* Konfeti parçacıkları */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {particles.map(p => (
          <div key={p.id} style={{
            position: 'absolute', left: `${p.x}%`, top: '-10px',
            width: p.size, height: p.size, borderRadius: p.id % 3 === 0 ? '50%' : '2px',
            background: p.color, opacity: 0.8,
            animation: `confettiFall ${p.duration}s ease-out ${p.delay}s forwards`,
          }} />
        ))}
      </div>

      <div className="celebration-icon" style={{ position: 'relative', zIndex: 2 }}>🎉</div>
      <div className="celebration-text" style={{ position: 'relative', zIndex: 2 }}>Görev Tamamlandı!</div>
      <div className="celebration-sub" style={{ position: 'relative', zIndex: 2 }}>{result.feedback.feedback_message}</div>

      {/* XP kazanım — büyük ve parlak */}
      <div className="celebration-xp" style={{ position: 'relative', zIndex: 2, animation: 'xpPop 0.6s ease 0.3s both' }}>+{xpEarned} XP</div>

      {/* Truth Bonus — "Samimiyetinle kazandın!" altın parıltılı */}
      {truthDelta > 0 && (
        <div style={{ position: 'relative', zIndex: 2, marginBottom: 16, animation: 'fadeSlideUp 0.5s ease 0.5s both' }}>
          {/* Altın parıltı parçacıkları */}
          <div style={{ position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)', width: 200, height: 60, pointerEvents: 'none', overflow: 'visible' }}>
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${20 + Math.random() * 60}%`,
                top: `${Math.random() * 100}%`,
                width: 3 + Math.random() * 4,
                height: 3 + Math.random() * 4,
                borderRadius: '50%',
                background: i % 2 === 0 ? '#fbbf24' : '#f59e0b',
                animation: `truthSparkle ${1 + Math.random() * 1.5}s ease-in-out ${Math.random() * 0.8}s infinite`,
                opacity: 0,
              }} />
            ))}
          </div>
          <div style={{
            padding: '14px 24px', borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.1))',
            border: '1px solid rgba(251,191,36,0.35)',
            boxShadow: '0 0 24px rgba(251,191,36,0.2), inset 0 0 20px rgba(251,191,36,0.05)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, marginBottom: 4, animation: 'truthBadgePulse 2s ease-in-out infinite' }}>💎</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fbbf24', textShadow: '0 0 12px rgba(251,191,36,0.5)', letterSpacing: 0.5, animation: 'truthTextGlow 2s ease-in-out infinite' }}>
              Samimiyetinle Kazandın!
            </div>
            <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600, marginTop: 4 }}>
              🛡️ +{Math.round(truthDelta * 10)} Dürüstlük Puanı
            </div>
          </div>
        </div>
      )}

      {/* Streak badge — glow efektiyle */}
      {streakCount >= 2 && (
        <div className="streak-badge" style={{
          position: 'relative', zIndex: 2, fontSize: 16, padding: '10px 24px',
          boxShadow: streakCount >= 5 ? '0 0 20px rgba(245,158,11,0.4)' : 'none',
          animation: 'fadeSlideUp 0.5s ease 0.7s both',
        }}>
          🔥 {streakCount} görev serisi!{streakCount >= 5 ? ' Efsanevi!' : streakCount >= 3 ? ' Harika!' : ''}
        </div>
      )}

      {/* Motivasyon bilimi notu */}
      <div style={{ position: 'relative', zIndex: 2, fontSize: 12, color: 'var(--text-muted)', marginTop: 16, maxWidth: 280, textAlign: 'center', animation: 'fadeSlideUp 0.5s ease 0.9s both' }}>
        {streakCount >= 3 ? '📊 Dopamin döngün güçleniyor — her tamamlanan görev bir sonrakini kolaylaştırır.' : xpEarned >= 30 ? '🧠 Zorlu görevler beyinde daha güçlü bağlantılar oluşturur.' : '🌱 Her küçük adım nöral yol açar.'}
      </div>
    </div>
  );
}

// ============================================================
// ONBOARDING
// ============================================================
function OnboardingScreen({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState<'assessment' | 'mini_report' | 'first_task'>('assessment');
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [mini, setMini] = useState<any>(null);
  const [task, setTask] = useState<any>(null);
  const [ld, setLd] = useState(false);

  useEffect(() => {
    (async () => { try { const r = await startOnboarding(STUDENT_ID); if (r.status === 'already_completed') { onComplete(); return; } setStep(r.step || 'assessment'); if (r.questions) setQuestions(r.questions); if (r.mini_report) setMini(r.mini_report); } catch {} })();
  }, [onComplete]);

  const submitA = async () => { setLd(true); try { const r = await submitAssessment(STUDENT_ID, answers); setMini(r.mini_report); setStep('mini_report'); } catch {} setLd(false); };
  const getT = async () => { setLd(true); try { const r = await getFirstTask(STUDENT_ID); setTask(r.first_task); setStep('first_task'); } catch {} setLd(false); };

  if (ld) return <div className="loading"><div className="loading-spinner" /></div>;

  const steps = ['assessment', 'mini_report', 'first_task'];
  const ci = steps.indexOf(step);

  return (
    <div className="animate-slide-up">
      <div className="screen-header">
        <div className="screen-title">Hoş Geldin!</div>
        <div className="screen-subtitle">
          {step === 'assessment' && 'Seni tanıyalım — 5 hızlı soru'}
          {step === 'mini_report' && 'Seni analiz ettim, işte ilk bulgular'}
          {step === 'first_task' && 'İlk görevin hazır!'}
        </div>
      </div>
      <div className="onboarding-dots">
        {steps.map((s, i) => <div key={s} className={`onboarding-dot ${i === ci ? 'active' : i < ci ? 'done' : 'pending'}`} />)}
      </div>

      {step === 'assessment' && (<div>
        {questions.map((q: any) => (
          <div key={q.id} className="card" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>{q.question}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {q.options?.map((o: string) => <button key={o} onClick={() => setAnswers(p => ({ ...p, [q.id]: o }))} className={`tab-pill ${answers[q.id] === o ? 'active' : ''}`} style={{ padding: '6px 14px', fontSize: 12 }}>{o}</button>)}
            </div>
          </div>
        ))}
        <button onClick={submitA} disabled={Object.keys(answers).length < questions.length} className="btn btn-primary" style={{ marginTop: 8 }}>Analiz Et</button>
      </div>)}

      {step === 'mini_report' && mini && (<div>
        <div className="card-glow" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>İlk Analizin</div>
          <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>{mini.first_insight}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div className="card"><div style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600, marginBottom: 8 }}>Güçlü Yanların</div>{mini.strengths?.map((s: string, i: number) => <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>✓ {s}</div>)}</div>
          <div className="card"><div style={{ fontSize: 12, color: 'var(--warning)', fontWeight: 600, marginBottom: 8 }}>Gelişim Alanların</div>{mini.improvement_areas?.map((s: string, i: number) => <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>→ {s}</div>)}</div>
        </div>
        <div className="mood-banner" style={{ marginBottom: 16 }}><div className="mood-text">{mini.motivation_message}</div></div>
        <button onClick={getT} className="btn btn-primary">İlk Görevimi Ver!</button>
      </div>)}

      {step === 'first_task' && task && (<div>
        <div className="card-glow" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{task.title}</div>
          <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>{task.description}</p>
          <div style={{ display: 'flex', gap: 14, marginTop: 14, fontSize: 13 }}>
            <span>⏱ {task.duration_min} dk</span>
            <span style={{ color: 'var(--xp-gold)', fontWeight: 600 }}>+{task.xp_reward} XP</span>
          </div>
        </div>
        <button onClick={onComplete} className="btn btn-primary">Başla!</button>
      </div>)}
    </div>
  );
}

// ============================================================
// RETENTION — Achievements
// ============================================================
function RetentionScreen() {
  const [data, setData] = useState<any>(null);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [ld, setLd] = useState(true);

  useEffect(() => { (async () => { try { const [r, m] = await Promise.all([getRetentionStatus(STUDENT_ID), getRetentionMessages(STUDENT_ID)]); setData(r); setMsgs(m.messages || []); } catch {} setLd(false); })(); }, []);

  if (ld) return <div className="loading"><div className="loading-spinner" /></div>;
  if (!data) return <div className="empty-state"><p>Veri yüklenemedi</p></div>;

  return (
    <div className="animate-slide-up">
      <div className="screen-header"><div className="screen-title">Başarımlarım</div><div className="screen-subtitle">Kazandıkların burada</div></div>
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-value">🔥 {data.streak_current}</div><div className="stat-label">Günlük Seri</div></div>
        <div className="stat-card"><div className="stat-value">{data.streak_best}</div><div className="stat-label">En İyi Seri</div></div>
        <div className="stat-card"><div className="stat-value">{data.total_active_days}</div><div className="stat-label">Aktif Gün</div></div>
      </div>

      {data.weekly_stats?.activity_chart?.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Bu Hafta</div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 70 }}>
            {data.weekly_stats.activity_chart.map((d: any, i: number) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: Math.max(6, (d.tasks || 0) * 10), background: 'var(--accent-gradient)', borderRadius: 4, marginBottom: 6, boxShadow: d.tasks > 0 ? '0 0 8px var(--accent-glow)' : 'none' }} />
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{d.date?.slice(8, 10)}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>{data.weekly_stats.tasks_done} görev · {data.weekly_stats.minutes_active} dk</div>
        </div>
      )}

      {msgs.length > 0 && (<div style={{ marginBottom: 20 }}><div className="section-title" style={{ marginBottom: 8 }}>Mesajlar</div>
        {msgs.map((m: any) => <div key={m.id} className="card" style={{ marginBottom: 8, borderLeft: '3px solid var(--info)' }}><div style={{ fontSize: 13, marginBottom: 6 }}>{m.content}</div><button className="btn-ghost" style={{ fontSize: 12 }} onClick={async () => { await markMessageRead(m.id); setMsgs(p => p.filter(x => x.id !== m.id)); }}>Okundu ✓</button></div>)}
      </div>)}

      <div className="section-header"><div className="section-title">Başarımlar ({data.achievements?.total_earned || 0}/{data.achievements?.total_available || 0})</div></div>
      {data.achievements?.earned?.map((a: any) => (
        <div key={a.id} className="achievement-card earned"><div className="achievement-icon gold">🏆</div><div className="achievement-info"><div className="achievement-name">{a.name}</div><div className="achievement-desc">{a.description}</div></div><div className="achievement-xp">+{a.xp} XP</div></div>
      ))}
      {data.achievements?.available?.length > 0 && (<><div className="section-title" style={{ marginTop: 20, marginBottom: 8, color: 'var(--text-muted)' }}>Sonraki Hedefler</div>
        {data.achievements.available.map((a: any) => <div key={a.id} className="achievement-card locked"><div className="achievement-icon locked-icon">🔒</div><div className="achievement-info"><div className="achievement-name">{a.name}</div><div className="achievement-desc">{a.description}</div></div></div>)}
      </>)}
    </div>
  );
}

// ============================================================
// METRICS DASHBOARD
// ============================================================
function MetricsDashboardScreen() {
  const [data, setData] = useState<any>(null);
  const [ld, setLd] = useState(true);

  useEffect(() => { (async () => { try { setData(await getMetricsDashboard()); } catch {} setLd(false); })(); }, []);

  if (ld) return <div className="loading"><div className="loading-spinner" /></div>;
  if (!data) return <div className="empty-state"><p>Metrik verisi yüklenemedi</p></div>;

  return (
    <div className="animate-slide-up">
      <div className="screen-header"><div className="screen-title">Metrik Dashboard</div><div className="screen-subtitle">Sistem geneli istatistikler</div></div>
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--accent-light)' }}>{data.dau || 0}</div><div className="stat-label">Günlük Aktif</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--info)' }}>{data.wau || 0}</div><div className="stat-label">Haftalık Aktif</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--success)' }}>{data.mau || 0}</div><div className="stat-label">Aylık Aktif</div></div>
        <div className="stat-card"><div className="stat-value">{data.retention_rate ? `${Math.round(data.retention_rate * 100)}%` : 'N/A'}</div><div className="stat-label">Retention</div></div>
      </div>
      {data.today && <div className="card" style={{ marginBottom: 20 }}><div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Bugünün Detayları</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{Object.entries(data.today).map(([k, v]) => <div key={k} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}><div style={{ fontSize: 16, fontWeight: 600 }}>{String(v)}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k.replace(/_/g, ' ')}</div></div>)}</div>
      </div>}
      {data.dau_chart?.length > 0 && <div className="card"><div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>DAU Trend</div>
        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 80 }}>{data.dau_chart.map((d: any, i: number) => <div key={i} style={{ flex: 1, textAlign: 'center' }}><div style={{ height: Math.max(4, (d.value || 0) * 8), background: 'var(--accent-gradient)', borderRadius: 3, marginBottom: 4 }} /><div style={{ fontSize: 8, color: 'var(--text-muted)' }}>{d.date?.slice(5)}</div></div>)}</div>
      </div>}
    </div>
  );
}
